/**
 * Street Dice Legends - Client Entry Point
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Engine } from './engine/Engine';
import { AudioManager } from './engine/audio/AudioManager';
import { sound } from './engine/audio/sound';
import { installUiClickSounds } from './ui/uiClickSounds';
import { SharedRenderer } from './engine/render/SharedRenderer';
import { renderScheduler } from './engine/render/RenderScheduler';
import { adaptiveResolution } from './engine/render/AdaptiveResolution';
import gsap from 'gsap';
import { AssetLoader } from './engine/loaders/AssetLoader';
import { prewarmObjectVisibility } from './engine/render/Prewarm';
import { prewarmShaders } from './engine/render/precompile';
import { DiceController } from './game/dice/DiceController';
import { TutorialController } from './game/tutorial/TutorialController';
import { OfferDiceTryController } from './game/offer-try/OfferDiceTryController';
import { EquippedDiceTryController } from './game/dice-try/EquippedDiceTryController';
import { GameStateManager, GameScene, CameraController, GameFlowController } from './game';
import { SceneManager } from './game/scene/SceneManager';
import { UIManager, HomeScreen, DiceScreen, CrewScreen, StoreScreen, LobbyScreen, NotificationsScreen, LoginScreen, SettingsScreen, PlayerProfilesOverlay, SidebetButtonsOverlay, CancelBetOverlay, SidebetCancelButtons, CountdownOverlay, InstructionBanner, InGameMenu } from './ui';
import { DEFAULT_AVATAR } from './ui/screens/HomeScreen';
import { InGameChat } from './ui/components/chat/InGameChat';
import { soloMultiplayerButton } from './ui/components/SoloMultiplayerButton';
import { soloTablesOverlay } from './ui/components/SoloTablesOverlay';
import { soloGameSelect } from './ui/components/SoloGameSelectOverlay';
import { soloStacksCounter } from './ui/components/SoloStacksCounter';
import { soloRollHistoryOverlay } from './ui/components/SoloRollHistoryOverlay';
import { ManualOverlay } from './ui/components/manual/ManualOverlay';
import type { ManualSectionId } from './ui/components/manual/manualContent';
import { VoiceClient } from './network/voice/VoiceClient';
import { settingsStore, type SettingsState } from './settings/SettingsStore';
import { LobbyService } from './services/LobbyService';
import { CreateRoomModal } from './ui/components/lobby/CreateRoomModal';
import { LoadingScreen } from './ui/LoadingScreen';
import { uuid } from './util/uuid';
import { BettingController, BettingHUD3D } from './game/betting';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { isIOSNative, applyPlatformClasses } from './util/platform';
import { initWebViewport } from './util/webViewport';
import { installCrashReport } from './util/crashReport';
import { NetworkManager } from './network/NetworkManager';
import { serverConfig } from './network/ServerConfig';
import { DiceAssetManager } from './game/dice/assets/DiceAssetManager';
import type { AuthSuccessPayload, DiceGearUpdatedPayload, NotificationListPayload, GameNotification, TutorialState, Tutorial2State, Tutorial2TaskId, DailyChallengesState } from '@sdl/shared';
import { TaskBoardWidget, type TaskTarget } from './ui/components/TaskBoardWidget';
import { DailyChallengesWidget, type ChallengeClaimRequest } from './ui/components/DailyChallengesWidget';
import { flyGoldCoin } from './ui/components/goldFly';
import { ChatService } from './services/ChatService';
import { OfferService } from './services/OfferService';
import { OfferPopup } from './ui/components/OfferPopup';
import { isOfferHidden, recordAppLaunch, canShowStartupPopup, markStartupPopupShown } from './services/offerPrefs';
import { createAdProvider } from './iap/AdProvider';
import type { OfferWire } from '@sdl/shared';
import { diceIsTokenOnly, DEFAULT_DICE_ID } from '@sdl/shared';
import { BlockService } from './services/BlockService';
import { ChatNotificationBubble } from './ui/components/ChatNotificationBubble';
import { StandaloneChat } from './ui/components/crew/StandaloneChat';
import { PlayerCardController } from './ui/components/crew/PlayerCardController';
import { AvatarUploadController } from './ui/components/AvatarUploadController';
import { BonusSuitcaseOverlay } from './ui/components/BonusSuitcaseOverlay';
import { CoachMarkOverlay } from './ui/components/CoachMarkOverlay';
import { preloadCanvasFonts } from './ui/fontPreload';
import { showToast } from './ui/components/Toast';
import { FloatingDiceLibrary } from './ui/components/FloatingDiceLibrary';
import { WelcomeModal } from './ui/components/WelcomeModal';
import { CashRewardOverlay } from './ui/components/CashRewardOverlay';
import { DiceUnlockedModal } from './ui/components/DiceUnlockedModal';
import { TutorialRewardModal } from './ui/components/TutorialRewardModal';
import { DicePurchaseModal } from './ui/components/DicePurchaseModal';
import { fetchDiceStore, purchaseDice as purchaseStoreDice } from './api/diceStoreApi';
import { IapManager } from './iap/IapManager';
import { notificationService } from './services/NotificationService';
import { NotificationPrimer } from './ui/components/NotificationPrimer';
import { ratingService } from './services/RatingService';
import { RatingPrimer } from './ui/components/RatingPrimer';
import type { IapGranted, IapBalance } from './api/iapApi';
import { ComingSoonOverlay, type ComingSoonOptions } from './ui/components/ComingSoonOverlay';
import { UsernamePromptModal } from './ui/components/UsernamePromptModal';
import { ErrorModal } from './ui/components/ErrorModal';
import { ConfirmModal } from './ui/components/ConfirmModal';
import { BannedOverlay } from './ui/components/BannedOverlay';
import { googleSignIn, googleSignOut } from './auth/googleSignIn';
import { appleSignIn } from './auth/appleSignIn';
import { DebouncedUsernameChecker } from './utils/DebouncedUsernameChecker';
import { getNoteCdnUrl, fetchCurrencyNotes } from './api/currencyNoteApi';
import type { DiceItem, DiceStoreTile } from './ui/screens/DiceScreen';
import { FlyoutDebugPanel } from './ui/components/FlyoutDebugPanel';

// Physics debug visualization
let physicsDebugEnabled = false;
const physicsDebugMeshes: THREE.LineSegments[] = [];

function updatePhysicsDebug(physicsWorld: any, scene: THREE.Scene): void {
  if (!physicsDebugEnabled) {
    // Remove existing debug meshes
    for (const mesh of physicsDebugMeshes) {
      scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    physicsDebugMeshes.length = 0;
    return;
  }

  const world = physicsWorld.world as CANNON.World;
  const bodies = world.bodies;

  // Remove old meshes
  for (const mesh of physicsDebugMeshes) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }
  physicsDebugMeshes.length = 0;

  // Create wireframes for each body
  for (const body of bodies) {
    for (const shape of body.shapes) {
      let geometry: THREE.BufferGeometry | null = null;

      if (shape instanceof CANNON.Box) {
        const halfExtents = shape.halfExtents;
        geometry = new THREE.BoxGeometry(
          halfExtents.x * 2,
          halfExtents.y * 2,
          halfExtents.z * 2
        );
      } else if (shape instanceof CANNON.Sphere) {
        geometry = new THREE.SphereGeometry(shape.radius, 8, 6);
      } else if (shape instanceof CANNON.Plane) {
        geometry = new THREE.PlaneGeometry(20, 20);
      }

      if (geometry) {
        const edges = new THREE.EdgesGeometry(geometry);
        const material = new THREE.LineBasicMaterial({
          color: body.mass > 0 ? 0x00ff00 : 0x0088ff,
          depthTest: false,
          transparent: true,
          opacity: 0.7,
        });
        const wireframe = new THREE.LineSegments(edges, material);
        wireframe.position.copy(body.position as any);
        wireframe.quaternion.copy(body.quaternion as any);
        wireframe.renderOrder = 999;
        scene.add(wireframe);
        physicsDebugMeshes.push(wireframe);
        geometry.dispose();
      }
    }
  }
}

// Expose toggle function to window for debugging
(window as any).togglePhysicsDebug = () => {
  physicsDebugEnabled = !physicsDebugEnabled;
};
(window as any).showDiceInfo = () => {
  if (diceController) {
    const dice = diceController.getDice();
    console.log('[Debug] Dice info:', {
      count: dice.length,
      positions: dice.map(d => d.mesh.position.toArray()),
      inScene: dice.map(d => d.mesh.parent !== null),
    });
  } else {
  }
};


// Global instances
let engine: Engine | null = null;
let uiManager: UIManager | null = null;
let gameStateManager: GameStateManager | null = null;
let gameScene: GameScene | null = null;
let diceController: DiceController | null = null;
let cameraController: CameraController | null = null;
let homeScreen: HomeScreen | null = null;
let networkManager: NetworkManager | null = null;
let gameFlowController: GameFlowController | null = null;
let bettingController: BettingController | null = null;
let bettingHUD3D: BettingHUD3D | null = null;
let bettingAssetLoader: AssetLoader | null = null;
let playerProfilesOverlay: PlayerProfilesOverlay | null = null;
let countdownOverlay: CountdownOverlay | null = null;
let sidebetButtonsOverlay: SidebetButtonsOverlay | null = null;
let cancelBetOverlay: CancelBetOverlay | null = null;
let sidebetCancelButtons: SidebetCancelButtons | null = null;
let instructionBanner: InstructionBanner | null = null;
let inGameMenu: InGameMenu | null = null;
let inGameChat: InGameChat | null = null;
/** Lazily created on first open; reused for home + in-game manual access. */
let manualOverlay: ManualOverlay | null = null;
/** The game type of the live match (null when not in a game), so the in-game
 *  "How to Play" entry can open the manual to the relevant section. */
let activeGameType: 'ceelo' | 'craps' | 'solo' | null = null;
let voiceClient: VoiceClient | null = null;
let diceScreen: DiceScreen | null = null;
let floatingDiceLibrary: FloatingDiceLibrary | null = null;
let crewScreen: CrewScreen | null = null;
let notificationsScreen: NotificationsScreen | null = null;
let settingsScreen: SettingsScreen | null = null;
let shopScreen: StoreScreen | null = null;
let iapManager: IapManager | null = null;
let playerCardController: PlayerCardController | null = null;
let avatarUploadController: AvatarUploadController | null = null;
/** Local player's id — set on auth, used to skip self-taps on player cards. */
let localPlayerId: string | null = null;
/** JWT from auth:success — needed for authenticated HTTP calls (avatar upload). */
let authToken: string | null = null;
let chatService: ChatService | null = null;
/** Offer engine client cache (spec §8) — drives the StoreScreen tab + popup. */
let offerService: OfferService | null = null;
/** Full-screen tap-to-act offer popup (baked card image). */
let offerPopup: OfferPopup | null = null;
/** Latest live offer set + whether the startup popup has shown this session. */
let latestOffers: OfferWire[] = [];
let offerStartupShown = false;
/** Gate the startup popup: false for new players still in onboarding (set at auth
 *  from server tutorial state). The Store OFFERS tab is never gated. */
let startupOffersAllowed = false;

/** Show the startup offer popup once per session, after the home screen is up.
 *  Offers the player ticked "do not show this offer" on are filtered out of the
 *  startup push (they still appear in the StoreScreen OFFERS tab). */
function maybeShowStartupOffers(): void {
  if (offerStartupShown || !loaderDismissed || !offerPopup) return;
  if (!startupOffersAllowed) return; // new player still onboarding
  if (!canShowStartupPopup(Date.now())) return; // < 3 launches, or shown < 24h ago
  const visible = latestOffers.filter((o) => !isOfferHidden(o.id));
  // The server sends up to 4 paid offers so the Store OFFERS tab can show
  // several; the startup popup shows just ONE paid card. Keep all free/video,
  // plus the first paid (the paid list is shuffled server-side, so "first" is a
  // random paid offer).
  const firstPaid = visible.find((o) => o.kind === 'paid');
  const popupOffers = visible.filter((o) => o.kind !== 'paid');
  if (firstPaid) popupOffers.push(firstPaid);
  if (popupOffers.length === 0) return;
  offerStartupShown = true;
  markStartupPopupShown(Date.now());
  offerPopup.show(popupOffers);
}
/** Source of truth for the local player's blocked list (lobby/chat/search/card). */
const blockService = new BlockService();
let createRoomModal: CreateRoomModal | null = null;
let welcomeModal: WelcomeModal | null = null;
/** Set on first auth when the tutorial isn't done; consumed AFTER the loading
 *  screen dismisses so the Welcome modal animates in over the loaded home
 *  screen, not on top of the loader. */
let pendingWelcomeGate = false;
/** True once the boot loading screen has been dismissed. After that, a late
 *  auth:success (e.g. first social sign-in waiting on the username prompt
 *  outlasting the loader's 8s auth timeout) must show the Welcome gate
 *  itself — the boot-time consumer in main() has already run. */
let loaderDismissed = false;
let tutorialController: TutorialController | null = null;
/** Local "try the dice" demo launched from an offer card. */
let offerTryController: OfferDiceTryController | null = null;
/** Local "try" demo for the player's equipped loadout (from the Dice screen). */
let equippedTryController: EquippedDiceTryController | null = null;
let lobbyService: LobbyService | null = null;
let lobbyScreen: LobbyScreen | null = null;
/** Latest known local-player balance, updated on auth + balance:update. */
let localBalance: { stacks: number; gold: number; diceTokens: number } = { stacks: 0, gold: 0, diceTokens: 0 };
/** Local-player display info (for the cash-reward card). */
let localUsername = '';
let localPicture = '';
/** Connect-account bonus state: is the account linked, and is 100M already claimed. */
let localLinked = false;
let localConnectBonusClaimed = false;
/** Bridges the event-driven connect-bonus flyout into an awaitable: set by
 *  triggerConnectBonus(), resolved by the account:connect_bonus_result handler
 *  once presentBalanceGain has finished animating. */
let resolveConnectBonusFlyout: (() => void) | null = null;
/** True once the one-time auth:success subsystem init has run. A second
 *  auth:success (e.g. a guest linking a social account in-app re-emits a full
 *  AUTH_SUCCESS) must refresh state WITHOUT re-creating ChatService/crew/dice. */
let authInitialized = false;
/** The Stacks balance the player has already been *shown* (the single gate that
 *  prevents a gain being celebrated twice). Seeded on auth; bumped only by
 *  presentBalanceGain after it animates. */
let lastSeenBalance = 0;
/** Pending social sign-in chosen at the login screen (emitted after connect). */
let pendingSocialAuthMain: { provider: 'google' | 'apple'; token: string; tokenType: 'access' | 'id' } | null = null;
let usernamePrompt: UsernamePromptModal | null = null;
let bannedOverlay: BannedOverlay | null = null;
/** The shared debounced availability checker, exposed for the Settings rename
 *  editor (created during auth wiring where its result listener also lives). */
let usernameCheckerRef: DebouncedUsernameChecker | null = null;
const errorModal = new ErrorModal();
const confirmModal = new ConfirmModal();

/** Compact currency formatting for the store wallet pills (K/M/B/T). */
function formatCompact(n: number): string {
  if (n >= 1e12) return `${(n / 1e12).toFixed(n >= 1e13 ? 0 : 1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;
  return String(Math.floor(n));
}

/** Refresh the store-screen wallet pills from the latest local balance. */
function refreshShopWallet(): void {
  shopScreen?.updateWallet({
    stacks: formatCompact(localBalance.stacks),
    gold: localBalance.gold.toLocaleString('en-US'),
    diceTokens: String(localBalance.diceTokens),
  });
}

/** Gold-coin / dice-token note art for the IAP flyout (no CDN bill exists for
 *  these — reuse the same flyout with these icon notes). Gold uses the crowned
 *  coin, the game-wide gold icon. */
const GOLD_NOTE_URL = '/assets/icons/currency/gold.svg';
const TOKEN_NOTE_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect x="4" y="4" width="24" height="24" rx="6" fill="#ff2e8a" stroke="#c00060" stroke-width="2"/><g fill="#fff"><circle cx="11" cy="11" r="2"/><circle cx="21" cy="11" r="2"/><circle cx="11" cy="21" r="2"/><circle cx="21" cy="21" r="2"/><circle cx="16" cy="16" r="2"/></g></svg>',
);

function pillCenter(r: DOMRect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** Render a server-delivered IAP grant: fly cash / gold / tokens FROM the tapped
 *  bundle item TO their wallet pills using the same in-game cash flyout
 *  (flyoutCashWin), counting each pill up as the notes land. (Dice unlocks ride
 *  the dice-unlock modal once offers ship.) */
async function handleIapGranted(granted: IapGranted, balance: IapBalance, productId: string, offerId: string | undefined, isReal: boolean): Promise<void> {
  // Store-pill count-up endpoints. Real: server balance is the truth. Dev stub:
  // count from the current local value up by the grant (display-only).
  const startS = isReal
    ? { stacks: Math.max(0, balance.stacks - granted.stacks), gold: Math.max(0, balance.gold - granted.gold), diceTokens: Math.max(0, balance.diceTokens - granted.diceTokens) }
    : { stacks: localBalance.stacks, gold: localBalance.gold, diceTokens: localBalance.diceTokens };
  const endS = isReal
    ? { stacks: balance.stacks, gold: balance.gold, diceTokens: balance.diceTokens }
    : { stacks: localBalance.stacks + granted.stacks, gold: localBalance.gold + granted.gold, diceTokens: localBalance.diceTokens + granted.diceTokens };

  // REAL purchase only: adopt the server balance + mark the gain already-seen so
  // home doesn't replay its flyout. The dev stub must NOT touch these globals —
  // its fake balance would poison lastSeenBalance and break later real flyouts
  // (e.g. the daily bonus came out as a non-positive amount and got skipped).
  if (isReal) {
    localBalance = { stacks: balance.stacks, gold: balance.gold, diceTokens: balance.diceTokens };
    lastSeenBalance = balance.stacks;
  }

  // Offer purchase: keep the offer popup open and run its dedicated reward beat
  // (profile-card drop + money flyout + multi-dice unlock), then close the popup.
  // Dice land in the library automatically via player:gear_updated.
  if (offerId) {
    await runOfferPurchaseReward(granted, startS.stacks);
    if (isReal) {
      refreshShopWallet();
      homeScreen?.updatePlayerData({ stacks: balance.stacks, gold: balance.gold });
    }
    offerPopup?.endPurchase(true);
    return;
  }

  const overlay = playerProfilesOverlay;
  const bundle = shopScreen?.getBundleRect(productId);
  const spawn = bundle
    ? { x: bundle.left + bundle.width / 2, y: bundle.top + bundle.height / 2 }
    : { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };

  if (overlay && shopScreen) {
    overlay.setFlyoutAboveUI(true);
    overlay.show();

    // Cash → Stacks pill (real in-game bill art; default "+$X" label).
    if (granted.stacks > 0) {
      await fetchCurrencyNotes().catch(() => { /* fall back to icon */ });
      const noteUrl = getNoteCdnUrl(100_000_000) ?? getNoteCdnUrl(granted.stacks) ?? GOLD_NOTE_URL;
      const pill = shopScreen.getWalletPillRect('stacks');
      if (pill) {
        await overlay.flyoutCashWin('', granted.stacks, spawn, noteUrl,
          () => shopScreen?.countPillUp('stacks', startS.stacks, endS.stacks),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true });
      }
    }
    // Gold → Gold pill (coin note, "+N" label).
    if (granted.gold > 0) {
      const pill = shopScreen.getWalletPillRect('gold');
      if (pill) {
        // Gold count scales with the amount (default amount/500), capped.
        await overlay.flyoutCashWin('', granted.gold, spawn, GOLD_NOTE_URL,
          () => shopScreen?.countPillUp('gold', startS.gold, endS.gold),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true, labelText: `+${granted.gold.toLocaleString('en-US')}` });
      }
    }
    // Tokens → Token pill (token note, "+N" label).
    if (granted.diceTokens > 0) {
      const pill = shopScreen.getWalletPillRect('tokens');
      if (pill) {
        // 1 token = 1 icon (flyout caps at maxNotes).
        await overlay.flyoutCashWin('', granted.diceTokens, spawn, TOKEN_NOTE_URL,
          () => shopScreen?.countPillUp('tokens', startS.diceTokens, endS.diceTokens),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true, noteCountOverride: granted.diceTokens, labelText: `+${granted.diceTokens}` });
      }
    }

    // Let the last "+N" label linger before tearing down the overlay.
    await new Promise((r) => setTimeout(r, 700));
    overlay.hide();
    overlay.setFlyoutAboveUI(false);
  }

  if (isReal) {
    refreshShopWallet();
    // Keep home in sync for when the player navigates back.
    homeScreen?.updatePlayerData({ stacks: balance.stacks, gold: balance.gold });
  }
  // Dice grants only happen on offer purchases (handled in runOfferPurchaseReward);
  // static store packs grant currency only.
}

/** Reward beat shown over the still-open offer popup after a purchase: drop the
 *  profile card from the top + fly the Stacks onto it (count-up), then reveal the
 *  unlocked dice. Resolves when the player dismisses the dice modal. */
async function runOfferPurchaseReward(granted: IapGranted, startStacks: number): Promise<void> {
  // Above the offer popup (z 220): card at 230, flyout notes lifted above UI.
  if (granted.stacks > 0) {
    await showCashReward(granted.stacks, startStacks, { zIndex: 230, aboveUI: true });
  }
  if (granted.dice && granted.dice.length > 0 && diceCdnBase) {
    if (!diceUnlockedModal) diceUnlockedModal = new DiceUnlockedModal();
    const cdn = diceCdnBase;
    await diceUnlockedModal.show(granted.dice, (id) => `${cdn}/dice/${id}/thumb-128.png`);
  }
}

// ─── Dice Store (weekly rotation + gold/token unlock) ────────────────
let dicePurchaseModal: DicePurchaseModal | null = null;
let storeRewardModal: TutorialRewardModal | null = null;
let currentStoreTile: DiceStoreTile | null = null;

// ─── Tutorial Part 2 — home-screen onboarding task board ─────────────
let taskBoard: TaskBoardWidget | null = null;
let localTutorial: TutorialState | null = null;
let localTutorial2: Tutorial2State | null = null;
// Page-die reveals queued while in a match; flushed on the next home visit.
const pendingTutorial2Reveals: Array<{ page: 1 | 2; diceId: string }> = [];

// ─── Daily challenges — home-screen widget ───────────────────────────
// Pure render of server state: seeded by the auth snapshot, refreshed by
// every daily_challenges:update push (progress lands mid-match too — the
// main socket stays connected during games).
let dailyChallengesWidget: DailyChallengesWidget | null = null;
let localDailyChallenges: DailyChallengesState | null = null;

/** Fetch the weekly rotation and populate the store grid. */
async function loadDiceStore(): Promise<void> {
  if (!diceScreen || !diceCdnBase) return;
  const listing = await fetchDiceStore();
  if (!listing || !diceScreen || !diceCdnBase) return;
  const cdn = diceCdnBase;
  const tiles: DiceStoreTile[] = listing.dice.map((d) => ({
    id: d.diceId,
    thumbnailUrl: `${cdn}/dice/${d.diceId}/thumb-128.png`,
    goldPrice: d.goldPrice,
    tokenPrice: d.tokenPrice,
    // Trust the server flag, but fall back to deriving it from rarity so the
    // token-only display is correct even if the client outruns the server deploy.
    tokenOnly: d.tokenOnly ?? diceIsTokenOnly(d.rarity),
    rarity: d.rarity,
  }));
  diceScreen.setStoreDice(tiles);
}

/** Run the server unlock, then celebrate (owned grid + wallet refresh via the
 *  server's balance:update / player:gear_updated broadcasts). */
async function handleStorePurchase(currency: 'gold' | 'token'): Promise<void> {
  const tile = currentStoreTile;
  const modal = dicePurchaseModal;
  if (!tile || !modal || !authToken) return;

  modal.setBusy(true);
  const result = await purchaseStoreDice(tile.id, currency, authToken);
  if (result.ok) {
    modal.close();
    if (diceCdnBase) {
      // Celebrate with the tutorial's reward reveal (single die flips in over a
      // radial glow + confetti) — nicer than the multi-die grid pop.
      if (!storeRewardModal) storeRewardModal = new TutorialRewardModal();
      const reward = storeRewardModal;
      reward.show({
        heading: 'NEW DICE UNLOCKED',
        dieThumbnailUrl: `${diceCdnBase}/dice/${tile.id}/thumb-128.png`,
        dieLabel: tile.rarity ?? '',
        buttonLabel: 'COLLECT',
      });
      await new Promise<void>((resolve) => reward.once('continue', () => resolve()));
    }
  } else {
    modal.setBusy(false);
    modal.showError(storePurchaseError(result.error));
  }
}

/** Open (or re-open) the unlock modal for a store tile, seeded with the live
 *  wallet so the gold/token buttons gate on the current balance. */
function openPurchaseModal(tile: DiceStoreTile): void {
  if (!dicePurchaseModal) {
    dicePurchaseModal = new DicePurchaseModal();
    dicePurchaseModal.on('buy', ({ currency }) => { void handleStorePurchase(currency); });
    dicePurchaseModal.on('try', () => { void handleStoreTry(); });
  }
  currentStoreTile = tile;
  dicePurchaseModal.show({
    diceId: tile.id,
    thumbnailUrl: tile.thumbnailUrl,
    rarity: tile.rarity,
    goldPrice: tile.goldPrice,
    tokenPrice: tile.tokenPrice,
    tokenOnly: tile.tokenOnly,
    goldBalance: localBalance.gold,
    tokenBalance: localBalance.diceTokens,
  });
}

/** TRY a store die: hide the modal and load 3 of it in the shared shoot scene
 *  (same preview the dice library uses; the scene owns its own close button).
 *  When the player quits the preview, the unlock modal re-opens on the die. */
async function handleStoreTry(): Promise<void> {
  const tile = currentStoreTile;
  if (!tile) return;
  dicePurchaseModal?.close();
  await startEquippedTry([tile.id, tile.id, tile.id], () => openPurchaseModal(tile));
}

function storePurchaseError(code?: string): string {
  switch (code) {
    case 'insufficient_funds': return 'Not enough to unlock this one.';
    case 'already_owned': return 'You already own this die.';
    case 'not_in_store': return 'This die just left the store.';
    case 'token_only': return 'This one unlocks with a dice token only.';
    case 'network_error': return 'Connection lost. Try again.';
    default: return 'Could not complete the unlock. Try again.';
  }
}

let diceAssetManager: DiceAssetManager | null = null;
let diceCdnBase: string | null = null;
/** The dice-model catalog preload, captured so boot can wait for it before
 *  dismissing the loader (dice ready before the home screen). Null until auth. */
let modelsPreloadPromise: Promise<void> | null = null;
let chatBubble: ChatNotificationBubble | null = null;
let standaloneChat: StandaloneChat | null = null;
let loginScreen: LoginScreen | null = null;
let bonusSuitcaseOverlay: BonusSuitcaseOverlay | null = null;
/** One-time post-tutorial coach mark pointing at the daily-bonus gift button. */
let dailyBonusCoach: CoachMarkOverlay | null = null;
let pendingBonusPrize: number | null = null;
let pendingBonusMultiplier: number = 1;
/** Daily-bonus award awaiting the suitcase close → notes-to-pill animation. */
let pendingDailyAward: { amount: number; newBalance: number } | null = null;
let equipDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let authReady = false;
let pendingMatch = false;
let gameNetworkHandlersBound = false;
const loadingScreen = new LoadingScreen();

/**
 * Gritplay publisher splash (the static #gritplay-splash painted as the first web
 * frame in index.html, bridging the native-splash → web handoff). Guarantee it
 * stays up a minimum time, then fade + remove it. main() awaits this BEFORE
 * creating the game, so the splash isn't competing with the scene build and the
 * brand moment isn't cut short on a fast boot. No-op if the element is absent
 * (already removed, or running under HMR).
 */
const GRITPLAY_SPLASH_MIN_MS = 2000;
async function holdGritplaySplash(): Promise<void> {
  const el = document.getElementById('gritplay-splash');
  if (!el) return;
  // performance.now() is ms since the page loaded — i.e. how long the splash has
  // already been on screen — so we only wait out the remainder of the minimum.
  const remaining = GRITPLAY_SPLASH_MIN_MS - performance.now();
  if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
  el.classList.add('is-hiding');          // CSS opacity fade (0.55s)
  setTimeout(() => el.remove(), 600);
}
let sceneSetupResolve: (() => void) | null = null;
/** True between game:started and game:ended. Used by the resume handler
 *  to decide whether a foreground event should re-sync game state. */
let inGame = false;
/** Where returnToHomeFromGame lands after a game ends. The Solo "Tables"
 *  button sets this to 'lobby' so leaving solo drops into the room browser. */
let postGameNavTarget: 'home' | 'lobby' = 'home';
/** Unbind for the solo Tables button's live room-count listener. */
let soloTablesUnbind: (() => void) | null = null;
/** Set when a solo player taps JOIN on a table — fired after solo tears down. */
let pendingJoinRoomId: string | null = null;
/** Guard so overlapping visibility events don't stack resume flows. */
let resumeRefreshInFlight = false;

/**
 * Apply persisted user settings to the live engine subsystems. Idempotent —
 * safe to call on boot and again on every SettingsStore `change`.
 */
/**
 * Voice chat join gate. Re-enabled 2026-07-02: the installed-app lag was NOT
 * the mic/WebRTC mesh itself — it was cordova-plugin-playtalk's native
 * route-change listener force-holding the AVAudioSession in PlayAndRecord +
 * VoiceChat and fighting WebKit's own session management (see playTalk.ts for
 * the full story). playTalk is now a hard no-op, so the native plugin is
 * never instantiated and WebKit owns the session — the same configuration
 * Phone Dice runs voice on in production. Flip to false to kill voice again
 * (no mic is ever acquired when false; PTT UI hides via the same flag). */
const VOICE_CHAT_JOIN_ENABLED: boolean = true;

/**
 * Join the match voice mesh if voice chat is enabled and we're seated in a
 * game. Idempotent (VoiceClient.join guards re-entry), so it's safe to call
 * from the settings toggle and on every game:update.
 */
function maybeJoinVoice(): void {
  if (!VOICE_CHAT_JOIN_ENABLED) return; // voice disabled — see gate above
  if (activeGameType === 'solo') return; // solo grind is single-player — never open the mic
  if (!voiceClient || !networkManager) return;
  if (!settingsStore.get('audio').voiceChatEnabled) return;
  if (!networkManager.isInGame()) return;
  void voiceClient.join();
}

function applySettings(state: SettingsState): void {
  if (engine) {
    const a = state.audio;
    engine.audio.setVolume('master', a.master);
    engine.audio.setVolume('music', a.music);
    engine.audio.setVolume('sfx', a.sfx);
    engine.audio.setVolume('ui', a.ui);
    engine.audio.setVolume('voice', a.voice);
    if (a.muted !== engine.audio.isMuted()) {
      if (a.muted) engine.audio.mute();
      else engine.audio.unmute();
    }
  }

  // Voice chat: incoming-voice playback level + master enable/disable. The
  // WebRTC peer audio plays through its own <audio> elements (independent of
  // the Howler 'voice' bus above), so the slider is applied to VoiceClient too.
  const av = state.audio;
  voiceClient?.setPlaybackVolume(av.voice);
  // Mic-button behavior (hold to talk vs open-mic toggle). setMicMode drops
  // any latched-open mic on an actual mode change (its ptt:stop → setMuted).
  playerProfilesOverlay?.setMicMode(av.micMode);
  if (av.voiceChatEnabled) maybeJoinVoice();
  else {
    playerProfilesOverlay?.muteMic();
    voiceClient?.leave();
  }
  try {
    SharedRenderer.instance.setQuality(state.graphics.quality);
    // When a game is live, also re-apply to the scene lights so a quality
    // change actually resizes the shadow map (SharedRenderer.setQuality only
    // sets pixel ratio). No-op on menu screens where the engine isn't running.
    engine?.setQuality(state.graphics.quality);
  } catch {
    // Renderer not initialized yet (called before SharedRenderer.init) — the
    // init() call already seeds quality from the store, so this is harmless.
  }
}

/**
 * Open the floating StandaloneChat with a peer. Used by incoming-message
 * bubbles and by player cards opened OUTSIDE the crew modal (lobby / in-game),
 * where the embedded crew chat isn't on screen. Player cards opened from the
 * crew modal use the crew screen's own embedded chat instead.
 */
function openStandaloneChat(peerId?: string, peerUsername?: string, peerPicture?: string): void {
  if (!chatService || !localPlayerId) return;
  const container = document.getElementById('ui-container');
  if (!container) return;

  if (standaloneChat) {
    standaloneChat.destroy();
    standaloneChat = null;
  }
  standaloneChat = new StandaloneChat({
    container,
    chatService,
    localPlayerId,
    peerId,
    peerUsername,
    peerPicture,
    resolvePeer: (id) => crewScreen?.getFriend(id),
    getFavourites: () =>
      (crewScreen?.getFavouriteFriends() ?? []).map((f) => ({
        id: f.id,
        username: f.username,
        picture: f.picture,
        isOnline: f.isOnline,
      })),
  });
  standaloneChat.on('close', () => {
    standaloneChat = null;
  });
  // Tap the floating-chat header avatar → open that peer's player card.
  standaloneChat.on('profile:tap', ({ playerId, username, picture }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    playerCardController.show({ id: playerId, username: username ?? 'Player', picture });
  });
}

/**
 * Show login screen and wait for player to choose auth method
 */
function showLoginScreen(container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    loginScreen = new LoginScreen();
    container.appendChild(loginScreen.element);

    loginScreen.show();

    const proceed = () => {
      // Show loading screen, hide login behind it, then continue initialization
      loadingScreen.show();
      loginScreen?.hide();
      setTimeout(() => {
        loginScreen?.destroy();
        loginScreen = null;
        resolve();
      }, 400);
    };

    loginScreen.on('guest-login', () => {
      const token = uuid();
      localStorage.setItem('sdl_auth_token', token);
      localStorage.setItem('sdl_auth_type', 'guest');
      proceed();
    });

    const socialLogin = async (provider: 'google' | 'apple') => {
      // Google sign-in is not wired on the iOS binary yet (no iOS OAuth client /
      // reversed URL scheme). Intercept on iOS-native so the button shows a
      // friendly note instead of failing silently. Ships via the CDN, so it also
      // covers the build currently in App Review. Removed when the next iOS
      // build lands the native Google config.
      if (provider === 'google' && isIOSNative()) {
        errorModal.show('Google sign in', 'Google sign in is coming to iOS soon. For now, use Sign in with Apple or Play as Guest.');
        return;
      }
      let result;
      try {
        result = provider === 'google' ? await googleSignIn() : await appleSignIn();
      } catch (e) {
        console.error(`[${provider}-login] sign-in error`, e);
        const name = provider === 'google' ? 'Google' : 'Apple';
        errorModal.show('Sign-in failed', `Couldn’t sign in with ${name}. Please try again.`);
        return;
      }
      if (!result) return; // user cancelled — stay on login screen
      // Ensure a persistent device token so AUTH_GUEST re-auths the (soon to be
      // linked) account on the next launch.
      if (!localStorage.getItem('sdl_auth_token')) {
        localStorage.setItem('sdl_auth_token', uuid());
        localStorage.setItem('sdl_auth_type', provider);
      }
      pendingSocialAuthMain = { provider, token: result.token, tokenType: result.tokenType };
      proceed();
    };
    loginScreen.on('google-login', () => void socialLogin('google'));
    loginScreen.on('apple-login', () => void socialLogin('apple'));
  });
}

async function main() {

  // Tag <html> with the native platform (drives the Android backdrop-filter
  // disable below — blurring the live WebGL canvas under an animating overlay
  // flickers on Chromium WebView). Done first so any early overlay is covered.
  applyPlatformClasses();

  // Count this app launch (gates the startup offer popup to invested players).
  recordAppLaunch();

  // Build identifier — surface the running bundle hash so we can confirm from
  // the console (incl. Safari Web Inspector on the installed app) exactly which
  // deployed client the session loaded. The hash is the index-<hash>.js name the
  // CDN serves; it changes every deploy, so it's the OTA-version source of truth.
  // 'dev' when running the unhashed local Vite entry. Also on window.__build.
  const buildHash = import.meta.url.match(/index-([A-Za-z0-9_-]+)\.js/)?.[1] ?? 'dev';
  (window as any).__build = buildHash;
  console.log(`%c[SDL] client build: ${buildHash}`, 'color:#7CFC00;font-weight:bold');

  // Persist crash breadcrumbs across the iOS reload-on-crash and surface the
  // last one on screen (remote iOS debugging — we can't read the device console).
  installCrashReport();

  // Pull canvas-drawn fonts (Oswald et al.) into the font cache up front so the
  // first TSS/HUD label draw uses the real face instead of swapping in late.
  void preloadCanvasFonts();

  // Setup fullscreen immersive mode on native platforms
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.hide();
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch (e) {
      console.warn('StatusBar plugin not available:', e);
    }
  }
  // Android WebView reports env(safe-area-inset-top) as 0 in fullscreen even
  // though windowLayoutInDisplayCutoutMode=shortEdges draws under the camera
  // punch-hole, so the max(padding, env()) in .sdl-screen-content collapses
  // and the home pfp sits under the front camera. No safe-area plugin is
  // installed (adding one = native release, can't CDN-fix), so reserve a
  // status-bar-band fallback the CSS max()es in. iOS delivers real env()
  // values and stays on those.
  if (Capacitor.getPlatform() === 'android') {
    document.documentElement.style.setProperty('--safe-top-fallback', '40px');
  }

  // Browser delivery (app.streetdicelegends.com): force a portrait presentation
  // and attempt fullscreen. NO-OP inside the native shell (self-gated).
  initWebViewport();

  // Initialize the shared WebGL renderer (single context for the whole app).
  // Honor the user's saved graphics quality so older phones boot at their
  // chosen tier instead of always starting at 'high'.
  // Cap DPR in the native app: WKWebView has a far tighter GPU-memory budget than
  // desktop browsers, and the framebuffer (∝ DPR²) is the single biggest GPU
  // allocation. DPR 2→1.5 cuts framebuffer + render-target memory ~44%, the main
  // lever against the context-loss/black-render/crash seen only in the installed
  // app. Web/desktop are unaffected (no ceiling).
  SharedRenderer.init({
    quality: settingsStore.get('graphics').quality,
    // 2.0 in the native app (full retina). Framebuffer ∝ DPR², but DPR was never
    // the real budget problem — the actual ceiling was a 4096² dirt texture
    // embedded ×3 in the scene (~268MB VRAM), fixed in the scene data (resized to
    // 512²). With ~280MB of resident-scene VRAM reclaimed, DPR 2 fits comfortably
    // and reads crisp (1.5 was the safe interim, 1.0 was visibly pixelated). The
    // PMREM HalfFloat env hog is gone everywhere (raw/RGBA8 cubes), the scene is
    // resident (SceneManager, no churn), and aux scenes free their GPU on close.
    // If context loss ever returns, dial 2.0→1.5 here first, then MSAA/shadows.
    dprCeiling: Capacitor.isNativePlatform() ? 2.0 : undefined,
  });

  // Render-on-demand: keep the renderer at full rate while ANY gsap tween is
  // live. Nearly every 3D animation here is gsap (camera transitions, bill
  // drops, wallet, flyouts, dice-correction lerps), so this one hook covers
  // them all — no per-site code. Safe: it only ever pulses while a tween runs
  // (no infinite tweens exist), so a static board still drops to the idle
  // heartbeat. Pulse a hair past the frame so the gate never flickers.
  gsap.ticker.add(() => {
    if (gsap.globalTimeline.getChildren(false, true, true).length > 0) {
      renderScheduler.pulse(50);
    }
  });

  // Get containers
  const gameContainer = document.getElementById('game-container');
  const uiContainer = document.getElementById('ui-container');

  if (!gameContainer || !uiContainer) {
    throw new Error('Required containers not found');
  }

  // Hide game container initially (we start on the menu)
  gameContainer.style.visibility = 'hidden';

  // Hold the Gritplay splash for its minimum, THEN fade it out. Everything below
  // (login screen / engine + scene creation) is gated behind this, so the game
  // isn't built until the splash has had its moment. Renderer init above runs
  // behind the splash (productive work), which is fine — it's not "the game".
  await holdGritplaySplash();

  // Audio comes alive BEFORE the login screen so its buttons (and the welcome
  // modal that follows) actually click. The login screen blocks below until the
  // player picks an auth method, and the engine isn't built until after that —
  // so the AudioManager is created standalone here, handed to sound + the global
  // click delegator now, then reused by the engine (config.audio) so login and
  // in-game share one manager. Howler.autoUnlock lifts the AudioContext on the
  // first tap, so the very gesture that taps a login button also plays its sound.
  const audioManager = new AudioManager();
  sound.init(audioManager);
  installUiClickSounds();

  // Check for existing auth token
  const hasAuthToken = !!localStorage.getItem('sdl_auth_token');

  if (!hasAuthToken) {
    // No token — hide initial loading screen, show login, wait for guest choice
    await loadingScreen.hide();
    await showLoginScreen(uiContainer);
  }

  // Initialize engine
  engine = new Engine({
    container: gameContainer,
    audio: audioManager,
    renderer: {
      quality: settingsStore.get('graphics').quality,
      shadows: true,
    },
    physics: {
      gravity: 30,
    },
  });

  // One resident 3D environment for the whole session. The real game, the
  // tutorial, and both try-dice flows all reuse it via SceneManager.ensureScene
  // instead of building/tearing down their own scene — that churn (shader
  // compiles + texture uploads + shadow-map reallocs) is what black-rendered iOS.
  SceneManager.init(engine);

  // Dynamic resolution: keep full-retina (DPR 2) on devices that hold frame-rate,
  // and quietly ease DPR down only on devices that can't (older phones, iOS Low
  // Power Mode throttling) so the game stays SMOOTH rather than mysteriously
  // stuttering. Down is measured (sustained jank); up is gated (resume / charge /
  // long-stable probe with backoff) so it can never oscillate. No-op on devices
  // with no headroom to give. Fed per active frame from Engine.render().
  adaptiveResolution.start();

  // (sound.init + installUiClickSounds already ran pre-login against the shared
  // AudioManager the engine reused, so the login screen could click. Nothing to
  // re-init here.)

  // Deliberately NO native audio-session setup here. Calling the playtalk
  // plugin (even once) instantiates its native class, whose route-change
  // listener force-holds the session in PlayAndRecord + VoiceChat — the iOS
  // voice-lag root cause. WebKit manages the session itself (see playTalk.ts).

  // Push saved settings into the live subsystems now that engine + renderer
  // exist, then keep them in sync as the user changes them in Settings.
  applySettings(settingsStore.getAll());
  settingsStore.on('change', applySettings);

  // Initialize game state manager
  gameStateManager = new GameStateManager(engine);

  // Initialize UI
  uiManager = initializeUI(uiContainer);

  // When home becomes visible via tab nav, recap any un-shown balance gain
  // (delayed so the slide settles before the flyout lands on the pill).
  uiManager.on('screen:change', (data) => {
    const to = (data as { to?: string } | undefined)?.to;
    if (to === 'home') setTimeout(() => checkHomeBalanceGain(), 450);
  });

  // Flyout debug panel (Ctrl+Shift+F to toggle)
  new FlyoutDebugPanel(document.body);

  // Initialize network
  networkManager = new NetworkManager();

  // Body-level player card shared by lobby + in-game profile taps. Built here
  // (not in initializeUI) because it needs networkManager, which is created
  // after the UI. crewScreen + uiManager already exist by now.
  if (crewScreen) {
    const crew = crewScreen;
    playerCardController = new PlayerCardController({
      networkManager,
      blockService,
      lookupFriend: (id) => crew.getFriend(id),
      // Pending set lives on CrewScreen (seeded from friend:pending_list on
      // auth) — read it for the PENDING state and write back requests sent
      // from this card so search / crew-modal cards agree.
      lookupPending: (id) => crew.isPendingRequest(id),
      onFriendRequestSent: (info) => {
        crew.notePendingRequest(info);
        reportTutorial2Task('addFriend');
      },
      // Lobby / in-game cards open the floating standalone chat — the crew
      // modal isn't the active surface there. (Cards opened inside the crew
      // modal go through CrewScreen.showPlayerCard → embedded chat.)
      onChat: (id, username) => openStandaloneChat(id, username, crew.getFriend(id)?.picture),
    });
  }

  // Blocked list: server pushes the enriched list on auth and after every
  // block/unblock. BlockService fans changes out to the lobby (hide presence +
  // chat), the Settings manager, and the player card.
  networkManager.on('blocked:list', (data) => blockService.setAll(data.blocked));
  networkManager.on('friend:unblocked', (data) => blockService.remove(data.playerId));
  blockService.on('changed', ({ blocked }) => {
    lobbyService?.setBlockedIds(blockService.ids());
    settingsScreen?.setBlocked(blocked);
  });

  networkManager.on('auth:success', (data: AuthSuccessPayload) => {
    authReady = true;
    authToken = data.token;
    settingsScreen?.setSupportToken(data.token);
    const { player } = data;
    localPlayerId = player.id;
    localBalance = { stacks: player.balance.stacks, gold: player.balance.gold, diceTokens: (player.balance as { diceTokens?: number }).diceTokens ?? 0 };
    // Seed the Shop wallet pills from the auth balance. BALANCE_UPDATE only fires
    // on a CHANGE, so without this the pills sit at their init 0 until the first
    // win/grant (home HUD is seeded separately via homeScreen.updateFromServer).
    refreshShopWallet();
    localUsername = player.username;
    localPicture = (player as { picture?: string }).picture ?? '';
    localLinked = !!player.socialProvider;
    localConnectBonusClaimed = player.connectBonus?.claimed ?? false;
    localTutorial = player.tutorial ?? null;
    localTutorial2 = player.tutorial2 ?? null;
    localDailyChallenges = (player as { dailyChallenges?: DailyChallengesState }).dailyChallenges ?? null;
    // Yesterday's uncollected challenge gold was credited during auth
    // (rollover auto-bank) — tell the player so the balance jump reads as
    // earned, not glitched.
    if (localDailyChallenges?.autoBanked) {
      const banked = localDailyChallenges.autoBanked;
      window.setTimeout(() => {
        showToast(`Yesterday's challenge gold was added to your stash: +${banked}`, { type: 'success' });
      }, 1200);
    }
    // Seed the "seen" gate so the initial load doesn't animate the starting balance.
    lastSeenBalance = player.balance.stacks;
    // Tell the lobby who "you" are so chat bubbles align right when you speak.
    lobbyScreen?.setLocalUser({
      id: player.id,
      username: player.username,
      ringColor: 'gold',
    });
    // Populate the Settings → Account tab. Guest detection uses the server's
    // `Player####` naming until email/isGuest are added to the auth payload.
    // socialProvider is authoritative now that it's in the auth payload — a
    // guest is simply an account with no linked provider.
    settingsScreen?.setAccount({
      username: player.username,
      picture: (player as { picture?: string }).picture ?? '',
      isGuest: !player.socialProvider,
      // First rename on the account is free; the server reports prior renames.
      nameChangeFree: !(player as { usernameChanged?: boolean }).usernameChanged,
    });
    // Banned account → auth still succeeds, but lock the client to the ban
    // screen (server independently rejects every action). Covers the app on
    // top of the normal home setup that follows.
    if ((player as { banned?: boolean }).banned) enterBannedMode();
    else exitBannedMode();
    if (!homeScreen) return;
    homeScreen.updateFromServer(player);
    updateTaskBoard();
    updateDailyChallengesWidget();

    // Re-entry (a guest just linked an account in Settings): the heavy
    // subsystem init below already ran on the first auth. Just refresh state
    // and claim the 100M connect bonus — don't rebuild chat/crew/dice.
    if (authInitialized) {
      triggerConnectBonus();
      return;
    }
    authInitialized = true;

    // Push notifications. Wire deps, then SILENTLY register the token if the OS
    // permission is already granted. This NEVER prompts — the OS prompt only
    // fires later from the soft-ask flow (maybePrimeNotifications). onDeepLink
    // routes a tapped push to the right surface.
    notificationService.init({
      registerToken: (token, platform) => networkManager?.registerPushToken(token, platform),
      unregisterToken: (token) => networkManager?.unregisterPushToken(token),
      onDeepLink: (url) => routePushDeepLink(url),
    });
    void notificationService.syncIfAuthorized();

    // Start the rating age clock from first launch (idempotent).
    ensureRatingFirstSeen();

    // Populate DiceScreen from server gear data
    if (diceScreen && player.gear) {
      const { gear } = player;
      const items: DiceItem[] = gear.ownedDice.map(id => ({
        id,
        thumbnailUrl: `${gear.cdnBase}/dice/${id}/thumb-128.png`,
      }));
      diceScreen.setOwnedDice(items);
      diceScreen.setPresets(gear.presets);
      diceScreen.setActivePreset(gear.activePreset);

      // Create DiceAssetManager and preload ALL dice models at startup
      // (PhoneDice pattern: cache every GLB so cloning is instant during gameplay)
      if (gear.cdnBase) {
        diceCdnBase = gear.cdnBase;
        // Populate the weekly store rotation now that we have the CDN base.
        void loadDiceStore();
        diceAssetManager = new DiceAssetManager({
          cdnBase: gear.cdnBase,
          apiBaseUrl: serverConfig.mainServerUrl,
        });
        // Preload all base GLB models. Captured so boot waits for it before
        // dismissing the loader — the dice must be ready before the home screen.
        modelsPreloadPromise = diceAssetManager.preloadAllModels().catch((err) => {
          console.warn('[main.ts] Dice model catalog preload failed:', err);
        });
        // Also preload equipped dice documents (skin data — lightweight JSON).
        // The starter red die always rides along: it's the visual stand-in for
        // any empty/unloaded slot (acquireDiceOrDefault), so it must be ready
        // before the first dice spawn.
        const equippedIds = (gear.presets[gear.activePreset]?.slots.filter(Boolean) ?? []) as string[];
        diceAssetManager.preload([...new Set([DEFAULT_DICE_ID, ...equippedIds])]).catch((err) => {
          console.warn('[main.ts] Dice asset preload failed:', err);
        });
      }
    }

    // Offer engine: cache the live set the server pushed after auth, feed the
    // StoreScreen OFFERS tab, and surface the startup popup once the home screen
    // is up (free + video first, then the single random paid card).
    if (networkManager && !offerService) {
      offerService = new OfferService(networkManager);
      offerService.on('offers:changed', (offers) => {
        latestOffers = offers;
        shopScreen?.setOffers(offers);
        maybeShowStartupOffers();
      });
      offerService.on('offer:claim_result', (result) => {
        offerPopup?.notifyClaimResult(result);
      });
    }

    // Initialize Crew system
    if (crewScreen && networkManager) {
      chatService = new ChatService(networkManager, data.playerId);
      // Photo uploads go over REST and need the live JWT.
      chatService.setTokenProvider(() => authToken);
      // Settings → Account "Chat Storage" widget reads its numbers from here.
      settingsScreen?.setChatStorageProvider(async () => {
        if (!chatService) return null;
        const stats = await chatService.getStorageStats();
        return {
          chatBytes: stats.totalBytes,
          imageBytes: stats.imageBytes,
          messageCount: stats.messageCount,
        };
      });

      // The server sends only friend IDs on auth, then a separate enriched
      // `friend:list` event (handled in CrewScreen) with names/pictures/balances.
      // Start empty; the list event populates the crew tabs moments later.
      const favouriteIds = player.favourites ?? [];

      crewScreen.initialize({
        playerId: data.playerId,
        friends: [],
        favourites: favouriteIds,
        chatService,
        networkManager,
        blockService,
        // Daily gifts mint gold, so sending is connected-accounts-only. Live
        // getter: connecting mid-session (localLinked flips) unlocks gifting
        // without a re-init.
        isLinked: () => localLinked,
      });

      // Create notification bubble
      const bubbleContainer = document.getElementById('ui-container');
      if (bubbleContainer) {
        chatBubble = new ChatNotificationBubble({ container: bubbleContainer });

        // Show bubble when a message arrives and chat isn't already open
        chatService.on('message:received', async (msg) => {
          if (!chatService) return;
          // First time a DM lands from someone else is a concrete value moment —
          // prime notifications so they don't miss the next one. One-shot, gated.
          if (msg.from !== data.playerId) void maybePrimeNotifications();
          const isCrewVisible = uiManager?.getCurrentScreen() === 'crew';
          const isChatOpen = standaloneChat !== null;
          if (!isCrewVisible && !isChatOpen && msg.from !== data.playerId) {
            const count = chatService.unreadCount;
            chatBubble?.show({
              peerId: msg.from,
              peerUsername: msg.fromUsername,
              picture: msg.fromPicture,
              unreadCount: count,
            });
          }
        });

        // Tap bubble -> open standalone chat (shared opener)
        chatBubble.on('bubble:tap', ({ peerId }) => {
          chatBubble?.hide();
          openStandaloneChat(peerId);
        });
      }

      // Update nav badge from unread count
      chatService.on('unread:changed', ({ count: _count }: { count: number }) => {
        // TODO: UIManager dynamic badge API
        chatBubble?.updateBadge(_count);
      });
    }

    // Initialize notifications screen
    if (notificationsScreen && networkManager) {
      notificationsScreen.initialize(networkManager);
    }


    // Request home screen data (bonus availability, etc.)
    networkManager?.requestHomeData();

    // First-login tutorial gate. `tutorial` is server-authoritative state on
    // the player; if it's not yet completed, queue the Welcome modal. It's
    // shown only after loadingScreen.hide() resolves (see main()), so it
    // animates in over the loaded home screen instead of over the loader.
    // New players mid-onboarding (tutorial not completed) shouldn't get hit with
    // the startup offer popup — let them settle in first. They still see offers
    // in the Store tab, and the popup returns next session once onboarded.
    startupOffersAllowed = !(player.tutorial && !player.tutorial.completed);
    if (player.tutorial && !player.tutorial.completed) {
      pendingWelcomeGate = true;
      // Loader already gone (auth outlasted its 8s timeout — happens when the
      // first social sign-in sits on the username prompt) → show it now.
      if (loaderDismissed) {
        pendingWelcomeGate = false;
        showWelcomeGate();
      }
    }

    if (pendingMatch) {
      pendingMatch = false;
      networkManager?.findMatch({ gameType: 'ceelo', stakes: 'low' });
    }
  });

  // Home screen data (bonus availability)
  networkManager.on('home:data', (data) => {
    const { dailyBonus } = data;
    pendingBonusPrize = dailyBonus.prize;
    pendingBonusMultiplier = dailyBonus.multiplier;
    homeScreen?.updatePlayerData({
      hasDailyBonus: dailyBonus.available,
      bonusStreak: dailyBonus.streak,
      bonusMultiplier: dailyBonus.multiplier,
      nextBonusAt: dailyBonus.nextAvailableAt ?? null,
    });
    // Authoritative picture: self-corrects the home avatar + settings on every
    // home visit (fires on each navigate-to-home), so a moderator removal shows
    // even if the live pfp:removed event was missed. Empty => default/initials.
    if (data.picture !== undefined) {
      localPicture = data.picture;
      homeScreen?.updatePlayerData({ avatar: data.picture || DEFAULT_AVATAR });
      settingsScreen?.setAccount({ picture: data.picture });
    }
  });

  // Daily bonus claim result — stash the award; the notes-to-pill animation
  // plays when the suitcase closes (the count-up rides that, not this event).
  networkManager.on('daily_bonus:result', (data) => {
    if (data.success) {
      homeScreen?.updatePlayerData({ bonusStreak: data.streak, bonusMultiplier: data.multiplier });
      reportTutorial2Task('dailyBonus');
      pendingDailyAward = { amount: data.amount, newBalance: data.newBalance };
      // Note: the notification soft-ask is NOT fired here. It rides the END of
      // the cash flyout (suitcase 'close' → presentBalanceGain) so the primer
      // lands cleanly on the home screen after the money animation, not on top
      // of it.
    }
  });

  // Balance update from server (gifts, game payouts, etc.)
  networkManager.on('balance:update', (data) => {
    localBalance = { stacks: data.stacks, gold: data.gold, diceTokens: data.diceTokens ?? localBalance.diceTokens };
    createRoomModal?.setBalance(data.stacks);
    refreshShopWallet();
    const onHome = uiManager?.getCurrentScreen() === 'home';
    // A positive gain landing while we're on home — e.g. game winnings whose
    // settlement `balance:update` arrives AFTER we've returned to the lobby (so
    // checkHomeBalanceGain's one-shot check already passed) — gets the same
    // flyout the bonuses do. The lastSeenBalance gate dedupes against
    // checkHomeBalanceGain so it animates exactly once. Otherwise snap the pill.
    if (onHome && !bonusInFlight && data.stacks > lastSeenBalance) {
      void presentBalanceGain(data.stacks);
    } else if (bonusInFlight) {
      // A cash-award animation owns the Stacks pill — don't snap it (let the
      // count-up finish); gold still updates.
      homeScreen?.updatePlayerData({ gold: data.gold });
    } else {
      homeScreen?.updatePlayerData({ stacks: data.stacks, gold: data.gold });
    }
  });

  // Username change outcome. The Settings editor updates its own state via
  // onUsernameChangeResult; here we mirror the new name onto home + the local
  // cache and surface success/failure. Gold is handled by the BALANCE_UPDATE
  // the server sends alongside, so we don't touch it here.
  networkManager.on('username:change_result', (result) => {
    settingsScreen?.onUsernameChangeResult(result);
    if (result.ok && result.username) {
      localUsername = result.username;
      homeScreen?.updatePlayerData({ username: result.username });
      showToast(`You're now ${result.username}`, { type: 'success' });
      return;
    }
    const msg =
      result.reason === 'insufficient_gold' ? `Not enough Gold. A name change costs ${result.goldCost ?? ''} Gold.`
      : result.reason === 'taken' ? 'That name is taken. Try another.'
      : result.reason === 'reserved' ? 'That name is reserved. Pick another.'
      : result.reason === 'guest' ? 'Connect an account to change your name.'
      : result.reason === 'rate_limited' ? 'One moment, then try again.'
      : result.reason === 'invalid' ? 'Pick a valid name.'
      : result.reason === 'unchanged' ? ''
      : 'Could not change your name. Please try again.';
    if (msg) showToast(msg, { type: 'error' });
  });

  // Connect-account 100M bonus granted → notes fly to the home Stacks pill.
  networkManager.on('account:connect_bonus_result', (data) => {
    // Resolve any awaiter (triggerConnectBonus) once the flyout has finished —
    // or immediately on failure, so a failed claim never hangs the caller.
    const settle = () => { resolveConnectBonusFlyout?.(); resolveConnectBonusFlyout = null; };
    if (!data.success) { settle(); return; }
    localConnectBonusClaimed = true;
    localLinked = true;
    reportTutorial2Task('connectAccount');
    void presentBalanceGain(data.newBalance).then(settle);
  });

  // Server-side auth failures (e.g. a rejected social token) → surface to player.
  networkManager.on('auth:error', (data) => {
    const msg = data?.code === 'BAD_TOKEN'
      ? 'Couldn’t verify your Google sign-in. Please try again.'
      : 'Couldn’t sign you in. Please try again.';
    errorModal.show('Sign-in failed', msg);
  });

  // Notification events
  networkManager.on('notification:list', (data: NotificationListPayload) => {
    notificationsScreen?.setNotifications(data.notifications);
    homeScreen?.updatePlayerData({ notifications: data.unseenCount });
  });

  networkManager.on('notification:new', (notif: GameNotification) => {
    notificationsScreen?.addNotification(notif);
    // Increment badge — server will also send unseen_count, but this provides instant feedback
  });

  networkManager.on('notification:removed', (data: { notificationId: string }) => {
    notificationsScreen?.removeNotification(data.notificationId);
  });

  networkManager.on('notification:unseen_count', (data: { count: number }) => {
    homeScreen?.updatePlayerData({ notifications: data.count });
  });

  networkManager.on('player:gear_updated', (data: DiceGearUpdatedPayload) => {
    if (!diceScreen) return;
    // Owned-dice grant (tutorial/tasks): rebuild the grid from the full list.
    if (data.ownedDice && diceCdnBase) {
      const items: DiceItem[] = data.ownedDice.map(id => ({
        id,
        thumbnailUrl: `${diceCdnBase}/dice/${id}/thumb-128.png`,
      }));
      diceScreen.setOwnedDice(items);
      // Preload the newly-granted models so equipping them is instant.
      diceAssetManager?.preload(data.ownedDice).catch(() => { /* non-blocking */ });
    }
    if (data.presets) diceScreen.setPresets(data.presets);
    if (data.activePreset !== undefined) diceScreen.setActivePreset(data.activePreset);
  });

  // Tutorial part 2 task-board state. The owned-dice grid refresh rides the
  // separate player:gear_updated broadcast; here we update the board + reveal a
  // freshly-granted page die (deferred to the home screen if mid-match).
  networkManager.on('tutorial2:updated', (data) => {
    localTutorial2 = data.state;
    taskBoard?.applyState(data.state);
    if (data.granted) {
      pendingTutorial2Reveals.push(data.granted);
      flushTutorial2Reveals();
    } else {
      updateTaskBoard();
    }
  });

  // Daily-challenge state push: progress (including mid-match), claim
  // results, and the first state after tutorial2 completion. Pure render —
  // the gold pill moves via the separate BALANCE_UPDATE from the credit.
  networkManager.on('daily_challenges:update', (data) => {
    localDailyChallenges = data.state;
    updateDailyChallengesWidget();
  });

  networkManager.on('match:found', (payload) => {
    // Parse the actual gameType from the roomId (server-side
    // GameRoomManager.createRoom encodes it as `<gameType>_...`).
    const prefix = (payload.roomId ?? '').split('_')[0];
    const gameType: 'ceelo' | 'craps' | 'solo' =
      prefix === 'craps' ? 'craps' : prefix === 'solo' ? 'solo' : 'ceelo';
    if (gameType === 'solo') {
      // Keep the lobby subscription alive so the in-game Tables button can show
      // live rooms and pull the player into multiplayer when one opens. Subscribe
      // as solo: we receive room broadcasts but never surface as a profile card
      // in the public lobby (solo grinders play alone and can't be joined).
      if (networkManager) lobbyService?.connect(networkManager, { solo: true });
      // Cancel on the game-select screen → leave solo back to home.
      soloGameSelect.setOnCancel(() => exitMatchToHome());
      void startGame('matchmaking', 'solo').then(() => showSoloTablesButton());
    } else {
      // Real table: server already removed us from lobby presence; mirror that
      // so we stop receiving lobby broadcasts while in the game.
      lobbyService?.disconnect();
      startGame('matchmaking', gameType);
    }
  });
  networkManager.on('match:error', (error) => {
    console.warn('[main.ts] Match error:', error);
    // Unstick the player: drop the loader and tell them what happened. No screen
    // change happened on the way in (startGame only runs on match:found), so
    // hiding the loader returns them to the lobby/home they triggered from,
    // where they can try again.
    void loadingScreen.hide();
    showToast(friendlyMatchError(error), { type: 'error' });
  });
  // The room was allocated (match:found) but the game-server socket never
  // connected. Don't strand the player in a half-built scene: drop the loader,
  // explain, and tear the session down to a usable screen.
  networkManager.on('game:connect_failed', () => {
    console.warn('[main.ts] Game server connection failed');
    void loadingScreen.hide();
    showToast("Couldn't reach the table. Try again.", { type: 'error' });
    if (inGame) {
      exitMatchToHome();
    } else {
      uiManager?.navigateTo('lobby');
    }
  });

  // Server refused to seat us. Without this the join dies silently and the
  // player sits on the loading screen forever (the kicked-rejoin bug).
  networkManager.on('game:join_rejected', ({ code }) => {
    console.warn('[main.ts] Game join rejected:', code);
    void loadingScreen.hide();
    const message =
      code === 'ROOM_FULL' ? 'That table is full. Try another one.'
      : code === 'ALREADY_IN_GAME' ? 'You are still seated at another table. Give it a moment, then try again.'
      : code === 'INSUFFICIENT_FUNDS' ? 'Not enough Stacks for this table.'
      : "Couldn't join the table. Try again.";
    showToast(message, { type: 'error' });
    if (inGame) {
      exitMatchToHome();
    } else {
      uiManager?.navigateTo('lobby');
    }
  });
  // If the player chose Google at the login screen, sign in socially instead of
  // as a guest (linking the device token so they re-auth seamlessly).
  if (pendingSocialAuthMain) {
    networkManager.loginSocial({
      ...pendingSocialAuthMain,
      guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
    });
  }
  // Single active session: the same account just signed in on another device,
  // so this one was kicked. Tell the player and reload (a reload re-auths and
  // takes the session back here — last device to act wins, no silent ping-pong).
  let sessionReplacedShown = false;
  networkManager.on('session:replaced', () => {
    if (sessionReplacedShown) return;
    sessionReplacedShown = true;
    void (async () => {
      await confirmModal.show({
        title: 'Signed in on another device',
        message: 'Your account is now active on another device. Only one device can be signed in at a time.',
        confirmLabel: 'OK',
        hideCancel: true,
      });
      window.location.reload();
    })();
  });

  // Removed at a round boundary for being away (backgrounded too long). The
  // server already dropped our seat, so tear down to home (exitMatchToHome's
  // room:leave is a harmless no-op server-side, and it clears the reconnect
  // token so we don't auto-rejoin) and surface a heads-up over the home screen.
  // Ban lockdown overlay (created once). Sign out is the only action; it reuses
  // the same logout flow as Settings. A live admin ban/unban flips it in real
  // time; an already-banned account also enters this on auth:success below.
  bannedOverlay = new BannedOverlay();
  bannedOverlay.on('signout', () => { void performLogout(); });
  networkManager.on('player:banned', (data) => {
    if (data.banned) enterBannedMode();
    else exitBannedMode();
  });

  networkManager.on('game:kicked', () => {
    exitMatchToHome();
    void confirmModal.show({
      title: 'Removed for inactivity',
      message: "You were away too long, so the table moved on without you. Jump back in any time from the lobby.",
      confirmLabel: 'OK',
      hideCancel: true,
    });
  });

  networkManager.connectMain();

  // New social identity → prompt for a name, then resend with it. The prompt
  // runs a live availability check while typing (Phone Dice RequestTimer
  // pattern: debounced, stale responses dropped); the server re-validates on
  // submit and bounces with `takenName` if a race slipped through.
  const usernameChecker = new DebouncedUsernameChecker(
    (requestId, name) => networkManager?.checkUsername(requestId, name),
  );
  usernameCheckerRef = usernameChecker; // share with the Settings rename editor
  networkManager.on('username:check_result', (d) => {
    usernameChecker.handleResult(d.requestId, d.available, d.username);
  });
  const ensureUsernamePrompt = (): UsernamePromptModal => {
    if (usernamePrompt) return usernamePrompt;
    usernamePrompt = new UsernamePromptModal();
    const prompt = usernamePrompt;
    prompt.on('input', ({ name }) => {
      const trimmed = name.trim();
      // Blank falls back to the default name, and a linking guest keeping
      // their own current name is always allowed — no check for either.
      if (!trimmed || trimmed.toLowerCase() === prompt.getDefaultName().toLowerCase()) {
        usernameChecker.cancel();
        prompt.setAvailability('idle');
        return;
      }
      prompt.setAvailability('checking');
      usernameChecker.query(trimmed, (available) => {
        prompt.setAvailability(available ? 'available' : 'taken');
      });
    });
    return prompt;
  };
  // Console debug helper (same pattern as togglePhysicsDebug): pop the name
  // prompt for styling/UX work — live availability check included; submit is
  // inert (no pendingSocialAuthMain). showUsernamePrompt('Player1234')
  (window as any).showUsernamePrompt = (defaultName = 'Player1234') => {
    ensureUsernamePrompt().show(defaultName);
  };
  networkManager.on('auth:social_needs_username', (data) => {
    const prompt = ensureUsernamePrompt();
    prompt.show(data.defaultName);
    if (data.takenName) prompt.showTaken(data.takenName);
    prompt.once('submit', ({ name }) => {
      usernameChecker.cancel();
      const last = pendingSocialAuthMain;
      if (!last) return;
      networkManager?.loginSocial({
        ...last,
        username: name,
        guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
      });
    });
  });

  gameFlowController = new GameFlowController({
    networkManager,
  });

  const nm = networkManager;
  playerProfilesOverlay = new PlayerProfilesOverlay({
    container: uiContainer,
    serverNow: () => nm.serverNow(),
  });
  countdownOverlay = new CountdownOverlay({ container: uiContainer });

  // Wire overlays to the controller NOW, before any server broadcast can
  // arrive. Scene setup happens later and re-wires scene-dependent bits; the
  // overlays don't depend on the 3D scene, and if we wait until after scene
  // load, a STARTING broadcast that lands during GLB load would hit a null
  // overlay (showOrUpdate is optional-chained, so it silently no-ops) —
  // late-joining players would miss the countdown entirely.
  gameFlowController?.setPlayerProfilesOverlay(playerProfilesOverlay);
  gameFlowController?.setCountdownOverlay(countdownOverlay);

  // In-game menu (slide-up bottom sheet) opened from the local player's
  // profile-card hamburger badge. ONE global overlay, created once and reused
  // for every match — mounted on document.body (NOT the per-game #ui-container)
  // so nothing in the per-game UI lifecycle can detach, hide, or desync it. The
  // per-game profiles overlay simply points its badge at this persistent menu.
  if (!inGameMenu) {
    inGameMenu = new InGameMenu({
      container: document.body,
      // Borrow the real Settings audio panel so the in-game Sound view and the
      // Settings tab share one implementation + the same settingsStore.
      getSoundPanel: () => settingsScreen!.buildAudioPanel(),
    });
    inGameMenu.on('chat', () => openStandaloneChat());
    inGameMenu.on('matchchat', () => inGameChat?.openHistory());
    // Open the manual to the section for the game you're in (falls back to lobby).
    inGameMenu.on('manual', () => openManual(
      // Solo has no manual section yet; fall back to the lobby overview.
      activeGameType === 'ceelo' || activeGameType === 'craps' ? activeGameType : 'lobby',
    ));
    inGameMenu.on('exit', () => exitMatchToHome());
  }
  playerProfilesOverlay.on('menu:open', () => inGameMenu?.open());

  // In-game equipped-dice management: the active shooter taps their own gear
  // thumbs to pull the dice library out into a floating window (no UI dup — it
  // BORROWS the live DiceScreen content, TRY button hidden). The gate (active
  // roller + dice at rest, like the try screen) lives on the controller; a
  // locked / not-your-turn tap gets a clean pill instead.
  floatingDiceLibrary = new FloatingDiceLibrary({ container: uiContainer });
  playerProfilesOverlay.on('gear:tap', () => {
    if (!gameFlowController || !diceScreen || !floatingDiceLibrary) return;
    if (floatingDiceLibrary.isOpen()) return;
    const gate = gameFlowController.canLocalPlayerSwapDice();
    if (!gate.allowed) {
      showToast(
        gate.reason === 'hilo'
          ? 'Dice lock in once HI-LO starts.'
          : 'Dice are in play. Change them before your next throw.',
        { type: 'info', durationMs: 2200 },
      );
      return;
    }
    const content = diceScreen.getContentElement();
    if (!content) return;
    const ghost = diceScreen.getDragGhostElement();
    floatingDiceLibrary.show(content, ghost ? [ghost] : []);
  });
  // Server-driven phase change while the library is open (timer expiry, round
  // start) → close it so a stale scrim never blocks the felt. The dice-pickup
  // case needs no auto-close: the scrim sits over the felt while open, so the
  // player closes the library before they can grab dice.
  nm.on('game:update', () => {
    if (floatingDiceLibrary?.isOpen() && !gameFlowController?.canLocalPlayerSwapDice().allowed) {
      floatingDiceLibrary.close();
    }
  });

  // In-match public chat: green badge on the local avatar opens the compose
  // bar (free text + quick emotes); per-player bubbles ride the profile cards;
  // the menu's "Match Chat" item opens the history modal. Lives on the same
  // UI container, owns its own NetworkManager + overlay refs.
  if (networkManager) {
    inGameChat = new InGameChat({
      container: uiContainer,
      networkManager,
      profiles: playerProfilesOverlay,
      getLocalPlayerId: () => localPlayerId,
    });
  }

  // While voice joining is disabled (kill switch above), hide the mic / PTT UI
  // so testers don't see a dead control. Pure CSS, gated by the same flag — no
  // voice code removed; flipping VOICE_CHAT_JOIN_ENABLED back to true brings the
  // mic straight back. (Re-visiting voice properly via PTT-scoped mic later.)
  if (!VOICE_CHAT_JOIN_ENABLED && !document.getElementById('voice-ui-hidden-style')) {
    const s = document.createElement('style');
    s.id = 'voice-ui-hidden-style';
    s.textContent = '.profile-card__mic, .profile-card__mic-large { display: none !important; }';
    document.head.appendChild(s);
  }

  // Voice chat: the WebRTC mesh transport behind the existing PTT button and
  // the per-opponent speaker/mute icon. VAD speaking state drives the same
  // indicators the cards already render.
  voiceClient = new VoiceClient(nm, {
    onSpeaking: (peerId, speaking) => playerProfilesOverlay?.setSpeaking(peerId, speaking),
    onPeerMuteChange: (peerId, muted) => playerProfilesOverlay?.setPeerMuted(peerId, muted),
  });
  // Push-to-talk: hold to talk. resumeAudio() rides the press (a user gesture)
  // so iOS lifts the AudioContext autoplay block on the first hold.
  playerProfilesOverlay.on('ptt:start', () => { voiceClient?.resumeAudio(); voiceClient?.setMuted(false); });
  playerProfilesOverlay.on('ptt:stop', () => voiceClient?.setMuted(true));
  // Mic taps change the audio.micMode SETTING (single source of truth): write
  // the store here; the change event → applySettings → setMicMode applies the
  // latch visuals + hot/cold edges, and Settings > Audio reflects it live.
  playerProfilesOverlay.on('mic:mode_request', ({ mode }) => settingsStore.update('audio', { micMode: mode }));
  // Seed the mic-button mode from settings (applySettings keeps it in sync).
  playerProfilesOverlay.setMicMode(settingsStore.get('audio').micMode);
  // Tap an opponent's speaker icon to mute/unmute them locally (the
  // togglePeerMute → onPeerMuteChange callback repaints their icon).
  playerProfilesOverlay.on('peer:mute_toggle', ({ playerId }) => voiceClient?.togglePeerMute(playerId));
  // Auto-join the mesh once the server confirms we're seated (first game:update);
  // leave on disconnect. Both calls are idempotent.
  nm.on('game:update', () => maybeJoinVoice());
  // Teardown also mutes the mic (the open-mic preference itself survives —
  // the overlay's show() re-arms it when the next match's UI comes up).
  nm.on('game:disconnected', () => { playerProfilesOverlay?.muteMic(); voiceClient?.leave(); });
  voiceClient.setPlaybackVolume(settingsStore.get('audio').voice);

  // Setup game state event handlers
  setupGameStateHandlers();

  // Show the UI (ensure it's visible)
  uiManager.show();

  // Wait for auth before hiding loading screen (so home screen has player data)
  if (!authReady) {
    await new Promise<void>((resolve) => {
      const check = () => { if (authReady) resolve(); };
      networkManager!.on('auth:success', check);
      // Timeout fallback — don't block forever if server is down
      setTimeout(() => resolve(), 8000);
    });
  }

  // Keep the loader up until the dice-model catalog has finished preloading, so
  // the dice are ready before the home screen appears. Bounded by a timeout so a
  // stuck CDN fetch can never trap the player on the loading screen.
  if (modelsPreloadPromise) {
    await Promise.race([
      modelsPreloadPromise,
      new Promise<void>((resolve) => setTimeout(() => {
        resolve();
      }, 20000)),
    ]);
  }

  // Hide loading screen
  await loadingScreen.hide();
  loaderDismissed = true;

  // Offers may have arrived during boot — surface the startup popup now the
  // home screen is visible.
  maybeShowStartupOffers();

  // Now that the loader is gone and the home screen is visible, animate the
  // first-login Welcome modal in (queued during auth:success above).
  if (pendingWelcomeGate) {
    pendingWelcomeGate = false;
    showWelcomeGate();
  }

}

/**
 * Initialize the UI system
 */
function initializeUI(container: HTMLElement): UIManager {
  const ui = new UIManager({
    container,
    initialScreen: 'home',
  });

  // Create and register screens
  homeScreen = new HomeScreen();
  ui.registerScreen('home', homeScreen.element);

  // Tutorial part 2 onboarding task board — mounts into the home progression
  // slot, hidden until auth:success seeds eligible state.
  taskBoard = new TaskBoardWidget();
  const tbSlot = homeScreen.element.querySelector('.home-progression-slot');
  tbSlot?.appendChild(taskBoard.element);
  taskBoard.on('task', (raw) => {
    const { target } = raw as { task: Tutorial2TaskId; target: TaskTarget };
    routeTaskTarget(target);
  });

  // Daily challenges — same slot, below the task board. They sequence
  // naturally: the board shows while tutorial2 is incomplete, the widget only
  // once the server gate (tutorial2 done OR tutorial skipped) unlocks it.
  dailyChallengesWidget = new DailyChallengesWidget();
  tbSlot?.appendChild(dailyChallengesWidget.element);
  dailyChallengesWidget.on('claim', (raw) => {
    const { challengeId, gold, button } = raw as ChallengeClaimRequest;
    // Local-fire-then-server-confirm (gift-collect pattern): the coin flies
    // now; the gold pill itself only moves on the server's BALANCE_UPDATE,
    // and a lost claim race self-heals on the next daily_challenges:update.
    flyGoldCoin(button, gold);
    networkManager?.sendDailyChallengeClaim(challengeId);
  });

  // Dice screen (populated from server on auth:success)
  diceScreen = new DiceScreen();
  ui.registerScreen('dice', diceScreen.element);

  // Debounced equip: send after 500ms of no further equip events
  diceScreen.on('equip', (data) => {
    reportTutorial2Task('equipDice');
    if (equipDebounceTimer) clearTimeout(equipDebounceTimer);
    equipDebounceTimer = setTimeout(() => {
      equipDebounceTimer = null;
      const slots = diceScreen?.getPresetSlots(data.preset);
      if (slots) networkManager?.sendEquipDice(data.preset, slots);
    }, 500);
    // Live in-game reflection is immediate (not debounced) so the swap feels
    // instant — but only when editing the ACTIVE loadout (the dice in play).
    if (data.preset === diceScreen?.getActivePreset()) sendLiveEquippedDice(data.preset);
  });

  // Preset switch: flush any pending equip, then send switch
  diceScreen.on('preset:switch', (data) => {
    if (equipDebounceTimer) {
      clearTimeout(equipDebounceTimer);
      equipDebounceTimer = null;
      // Flush the pending equip for the *previous* preset (already committed locally)
    }
    networkManager?.sendSwitchPreset(data.preset);
    // The newly-active preset is now the in-play loadout — reflect it live.
    sendLiveEquippedDice(data.preset);

    // Preload the new preset's dice in the background
    if (diceAssetManager && diceScreen) {
      const slots = diceScreen.getPresetSlots(data.preset);
      if (slots) {
        const ids = slots.filter(Boolean) as string[];
        if (ids.length > 0) {
          diceAssetManager.preload(ids).catch(() => {});
        }
      }
    }
  });
  diceScreen.on('try', (data) => {
    const slots = (diceScreen?.getPresetSlots(data.preset) ?? []).filter(Boolean) as string[];
    void startEquippedTry(slots);
  });

  // Store: tap a not-owned die → open the unlock modal (gold or token).
  diceScreen.on('store:tap', ({ tile }) => { openPurchaseModal(tile); });

  crewScreen = new CrewScreen();
  ui.registerScreen('crew', crewScreen.element);
  // Onboarding "add a friend" task completes the moment a request is sent (not
  // gated on the other player accepting).
  crewScreen.on('friend:request_sent', () => reportTutorial2Task('addFriend'));

  // Lobby screen (matchmaking + public chat). Service is connected on
  // lobby entry / disconnected on exit via the screen:change handler below.
  lobbyService = new LobbyService();
  lobbyScreen = new LobbyScreen(lobbyService, {
    id: 'local',
    username: 'YOU',
    ringColor: 'gold',
  });
  ui.registerScreen('lobby', lobbyScreen.element);
  lobbyScreen.on('back:tap', () => ui.navigateTo('home'));
  lobbyScreen.on('help:tap', () => openManual('lobby'));
  // Body-level player card shared by lobby + in-game profile taps. The
  // controller itself is constructed in main() once networkManager exists
  // (initializeUI runs before that). These handlers read the module-level
  // `playerCardController` at tap time, by which point it's set.
  lobbyScreen.on('profile:tap', ({ playerId, username, picture }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    // Presence data wins; fall back to caller-provided info (e.g. a chat sender
    // who isn't in the lobby presence list because they're already in a game).
    const p = lobbyService?.getPlayer(playerId);
    playerCardController.show({
      id: playerId,
      username: p?.username ?? username ?? 'Player',
      picture: p?.picture ?? picture ?? '',
      isOnline: p?.status === 'online',
    });
  });

  // In-game: tap an opponent's profile card to open the same player card.
  playerProfilesOverlay?.on('profile:tap', ({ playerId, username, picture, crewTag }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    playerCardController.show({ id: playerId, username, picture, crewTag });
  });

  // Create-room modal: opens from CreateRoomPill, submits via LobbyService,
  // game handoff is the existing match:found → startGame path.
  createRoomModal = new CreateRoomModal();
  document.body.appendChild(createRoomModal.element);
  createRoomModal.on('submit', (payload) => {
    createRoomModal?.hide();
    loadingScreen.show();
    lobbyService?.submitCreateRoom(payload);
  });
  lobbyScreen.on('create_room:tap', () => {
    createRoomModal?.show({ balanceStacks: localBalance.stacks });
  });
  lobbyScreen.on('join_room:tap', () => {
    // Server reply (match:found) drives the actual transition; show loading
    // immediately so the user gets feedback while we wait for the ack.
    loadingScreen.show();
  });

  // Notifications screen (modal-only, not registered as a navigable screen)
  notificationsScreen = new NotificationsScreen();

  shopScreen = new StoreScreen();
  ui.registerScreen('shop', shopScreen.element);
  refreshShopWallet();

  // In-App Purchases: native via cordova-plugin-purchase, dev stub in the browser.
  // The SERVER delivers the goods (validates + credits); we order, forward the
  // receipt, finish(), and render the grant.
  iapManager = new IapManager({
    getToken: () => authToken,
    getBalance: () => ({ stacks: localBalance.stacks, gold: localBalance.gold, diceTokens: localBalance.diceTokens }),
    devGrant: (productId) => shopScreen?.getBundleAmounts(productId) ?? null,
    onError: (productId, message) => {
      console.warn('[iap] purchase failed:', productId, message);
      // Re-enable the offer popup if the failed purchase was started from it.
      offerPopup?.endPurchase(false);
      if (message !== 'cancelled') showToast('Purchase could not be completed. Try again');
    },
    onGranted: (granted, balance, ctx) => { void handleIapGranted(granted, balance, ctx.productId, ctx.offerId, ctx.real); },
    // Store loaded localized prices → upgrade the offer CTA from its USD fallback.
    onProductsUpdated: () => offerPopup?.refresh(),
  });
  void iapManager.init();

  shopScreen.on('purchase', (raw: unknown) => {
    const { productId } = raw as { productId: string };
    // Mirror the offer flow: on a device (TestFlight) fire the real StoreKit
    // purchase; in a non-native build (browser, no store) show the same
    // "browser preview" message offers show instead of the dev-stub reward.
    if (!Capacitor.isNativePlatform()) {
      void showBrowserPurchaseNotice();
      return;
    }
    void iapManager?.purchase(productId, { surface: 'store_browse' });
  });
  shopScreen.on('claim-chest', () => {
    // TODO: server roll + reward animation
  });

  // Offer popup: a baked card the player taps. Paid → IAP (same grant path as
  // the store); free/video → server claim. Tapping an OFFERS-tab card opens it.
  offerPopup = new OfferPopup({
    onPaid: (offer) => {
      if (offer.storeid) {
        void iapManager?.purchase(offer.storeid, {
          surface: offer.surface ?? 'offer_popup',
          offerId: offer.id,
          devDice: offer.dice, // browser dev-stub: drive the dice-unlock reveal
        });
      } else {
        offerPopup?.endPurchase(false); // no SKU → don't leave the popup locked
      }
    },
    onClaim: (offer, adsWatched) =>
      networkManager?.claimOffer(offer.id, adsWatched != null ? { adsWatched } : undefined),
    onTryDice: (offer) => void startDiceTry(offer),
    getPrice: (sku) => iapManager?.getDisplayPrice(sku) ?? null,
    canPurchase: () => Capacitor.isNativePlatform(),
    onPurchaseUnavailable: (offer) => void showBrowserPurchaseNotice(offer),
    adProvider: createAdProvider(),
  });
  shopScreen.on('offer:tap', (raw: unknown) => {
    const { offer } = raw as { offer: OfferWire };
    offerPopup?.show([offer]);
  });

  settingsScreen = new SettingsScreen();
  ui.registerScreen('settings', settingsScreen.element);

  // Reuse the sign-up prompt's debounced availability checker for the Settings
  // rename editor — same debounce + stale-response-drop guarantees, one shared
  // instance (the two are never typing at once). Its results already route
  // through the single 'username:check_result' listener above.
  settingsScreen.setUsernameChecker(
    (name, cb) => usernameCheckerRef?.query(name, (available) => cb(available)),
    () => usernameCheckerRef?.cancel(),
  );
  settingsScreen.on('account:change-username', ({ username }) => {
    networkManager?.changeUsername(username);
  });

  // Change profile picture: pick → crop → upload to Bunny CDN → refresh the
  // account card. Mirrors the UDG Poker flow (@capacitor/camera + croppie).
  avatarUploadController = new AvatarUploadController({
    container,
    getToken: () => authToken,
    onUploaded: (picture) => {
      settingsScreen?.setAccount({ picture });
      homeScreen?.updatePlayerData({ avatar: picture });
      localPicture = picture;
      reportTutorial2Task('setPfp');
    },
  });
  settingsScreen.on('account:change-picture', () => {
    avatarUploadController?.change();
  });
  // Guest tapped a provider in the "Connect & bag 100M" section. Run the social
  // sign-in, then link it onto the CURRENT guest (guestToken) so dice/progress
  // carry over. The server re-emits AUTH_SUCCESS (guarded above) and we claim
  // the 100M via triggerConnectBonus on that re-entry. New identities still go
  // through the existing auth:social_needs_username name prompt.
  settingsScreen.on('account:connect', async (raw) => {
    const { provider } = raw as { provider: 'google' | 'apple' };
    // Same iOS-native Google guard as the login screen (see socialLogin).
    if (provider === 'google' && isIOSNative()) {
      errorModal.show('Google sign in', 'Google sign in is coming to iOS soon. For now, use Sign in with Apple or Play as Guest.');
      return;
    }
    let result;
    try {
      result = provider === 'google' ? await googleSignIn() : await appleSignIn();
    } catch (e) {
      console.error(`[settings:connect] ${provider} sign-in error`, e);
      const name = provider === 'google' ? 'Google' : 'Apple';
      errorModal.show('Sign-in failed', `Couldn’t sign in with ${name}. Please try again.`);
      return;
    }
    if (!result) return; // user cancelled — stay put
    pendingSocialAuthMain = { provider, token: result.token, tokenType: result.tokenType };
    networkManager?.loginSocial({
      ...pendingSocialAuthMain,
      guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
    });
  });
  settingsScreen.on('account:logout', async () => {
    // Confirm first — logout wipes local state and forces a re-login.
    const ok = await confirmModal.show({
      title: 'Log out?',
      message: "You'll need to sign back in to keep playing.",
      confirmLabel: 'LOG OUT',
      cancelLabel: 'STAY',
      danger: true,
    });
    if (!ok) return;
    await performLogout();
  });
  settingsScreen.on('account:unblock', ({ playerId }) => {
    networkManager?.sendFriendUnblock(playerId);
    blockService.remove(playerId); // optimistic — server re-sends blocked:list
  });
  settingsScreen.on('account:clear-chat', async () => {
    if (!chatService) return;
    const ok = await confirmModal.show({
      title: 'Clear chat history?',
      message: 'Every message and photo will be deleted from this device. This cannot be undone.',
      confirmLabel: 'CLEAR',
      cancelLabel: 'KEEP',
      danger: true,
    });
    if (!ok) return;
    await chatService.clearAllChats();
    showToast('Chat history cleared', { type: 'success' });
    settingsScreen?.refreshChatStorage();
  });

  // Push notifications toggle. ON is an explicit ask, so prompting the OS here
  // is fine; if the player previously denied at the OS level, nudge them to
  // Settings (we can't re-open the system prompt). OFF mutes server-side.
  settingsScreen.on('notifications:toggle', async ({ enabled }) => {
    if (enabled) {
      const result = await notificationService.enable();
      if (result === 'denied') {
        showToast('Turn on notifications for Street Dice Legends in your phone Settings');
      }
    } else {
      await notificationService.disable();
    }
    // Re-read the real permission so the toggle reflects the actual result
    // (e.g. snaps back OFF if the OS prompt was denied).
    void settingsScreen?.refreshNotificationState();
  });

  // Support — feedback + tickets are submitted directly to main-server REST by
  // SettingsScreen (it holds the JWT via setSupportToken). Manual "Rate us" →
  // store listing (or native sheet fallback on iOS pre-id).
  settingsScreen.on('support:rate', () => {
    void ratingService.openStoreListing();
  });

  // Mirror the in-Settings Support badge onto the bottom-nav Settings button so
  // an unread admin reply is visible from anywhere, not just inside Settings.
  settingsScreen.on('support:unread-changed', ({ hasUnread }) => {
    uiManager?.setNavBadge('settings', hasUnread);
  });

  // Admin replied to a ticket → refresh the list so the unread badge + thread
  // update live while the app is open (offline players get an FCM push instead).
  networkManager?.on('support:ticket_reply', () => {
    void settingsScreen?.refreshTickets();
  });

  // Admin removed this player's profile picture → revert the account + home
  // avatar to the default. The "why" arrives as a persistent system
  // notification (main-screen inbox + bell), not a toast. The in-game card (if
  // seated) and lobby presence blank on their own via game:update /
  // lobby:player_updated.
  networkManager?.on('pfp:removed', () => {
    localPicture = '';
    settingsScreen?.setAccount({ picture: '' });   // falls back to initials
    homeScreen?.updatePlayerData({ avatar: DEFAULT_AVATAR });
  });

  // Handle home screen actions
  homeScreen.on('action', (data) => {
    const { action } = data as { action: string };
    handleHomeAction(action);
  });

  // Refresh home data every time the home screen becomes visible
  ui.on('screen:change', (raw: unknown) => {
    const data = raw as { from: string; to: string };
    if (data.to === 'home' && networkManager && authReady) {
      networkManager.requestHomeData();
      // Play any page-die reveals queued while the player was in a match.
      flushTutorial2Reveals();
    }
    if (data.to === 'crew') {
      crewScreen?.onShow();
    }
    if (data.from === 'crew' && data.to !== 'crew') {
      // Chat partners opened but never messaged drop off the sidebar.
      crewScreen?.onHide();
    }
    if (data.to === 'settings') {
      settingsScreen?.onShow();
    }
    if (data.from === 'settings' && data.to !== 'settings') {
      settingsScreen?.onHide();
    }
    // Lobby socket lifecycle: subscribe on entry, unsubscribe on any exit.
    // Server-side presence is cleared on disconnect as well, so this only
    // matters while the user is actively connected.
    if (data.to === 'lobby' && networkManager && authReady) {
      lobbyService?.connect(networkManager);
    } else if (data.from === 'lobby') {
      lobbyService?.disconnect();
    }
    // Reset the store scroll when leaving — next visit lands on the chest hero.
    if (data.from === 'shop') {
      shopScreen?.resetScroll();
    }
  });

  // Refresh home data / in-game state when app returns from background.
  // Mobile Capacitor suspends JS while backgrounded; on return we can't
  // trust in-memory state. We also flip the server-side isActive flag so
  // the server doesn't stall waiting on a backgrounded client's lock-in.
  document.addEventListener('visibilitychange', () => {
    if (!networkManager || !authReady) return;

    if (document.visibilityState === 'hidden') {
      // An open mic must never keep broadcasting from a pocketed /
      // backgrounded phone — mute the transmission (ptt:stop → setMuted →
      // the 10s idle release then frees the capture pipeline). The open-mic
      // preference survives; the visible branch re-arms it on resume.
      playerProfilesOverlay?.muteMic();
      if (inGame) {
        // Best-effort: tell the server we're gone before the OS kills the
        // runtime. On mobile this flush may not make it through, in which
        // case the server's existing disconnect timeout is the fallback.
        networkManager.sendActiveStatus(false);
      }
      return;
    }

    // Visible / foregrounded:
    // Always notify server of resume — server bumps lastLogin + records a
    // daily-login event if the session has been idle >8h, so a desktop
    // browser tab whose socket never dropped still counts toward DAU.
    networkManager.sendClientResume();
    // Re-pull offers on foreground — eligibility (e.g. first-open-of-day,
    // schedule windows) may have changed while backgrounded.
    offerService?.refresh();
    if (inGame) {
      // Resume processes state:full as a same-state onUpdate (no onEnter →
      // no overlay.show()), so re-arm open mic here after the hidden-branch
      // mute. No-op in push mode.
      playerProfilesOverlay?.reassertMicHot();
      // refreshGameStateFromServer reconnects the game channel first, then
      // flips active=true and requests state — so we never emit into a dead
      // socket (which would hang the resume). state:full is processed through
      // the state machine, so a backgrounded mid-drag roller has their dice
      // re-spawned automatically: the resumed roll state's onEnter sees the
      // local id in update.rollAuthorizedFor and calls setupLocalRoll. Roll
      // authorization is resume-safe STATE now — no manual re-authorize.
      refreshGameStateFromServer().catch((err) => {
        console.warn('[main] resume refresh failed:', err);
      });
    } else {
      // Outside a game (home, lobby, dice, shop, settings): if a newer client
      // was deployed to the CDN while we were backgrounded, reload to pick it
      // up. Safe here — we never reload mid ceelo/craps game (that branch above
      // does a soft state-refresh instead). If there's no update, just refresh
      // home data.
      void maybeReloadForClientUpdate().then((reloading) => {
        if (!reloading) networkManager?.requestHomeData();
      });
    }
  });

  return ui;
}

/**
 * OTA freshness check, run on resume OUTSIDE a game. Compares the bundle this
 * session is running (`import.meta.url` → assets/index-<hash>.js) against the
 * hash referenced by the live `index.html` (served no-store, always fresh from
 * the CDN). If they differ, a newer client is live → reload to pick it up.
 * Resolves true when a reload was triggered (caller then skips its own work).
 *
 * No-ops safely when there's no hash to read (local Vite dev serves /src/main.ts,
 * not a hashed bundle), on fetch failure, or when already up to date — so it
 * never loops: after the reload the new bundle's hash matches the live index.
 */
async function maybeReloadForClientUpdate(): Promise<boolean> {
  try {
    const current = import.meta.url.match(/index-([A-Za-z0-9_-]+)\.js/)?.[1];
    if (!current) return false; // dev / non-hashed entry — nothing to compare
    const resp = await fetch('/index.html', { cache: 'no-store' });
    if (!resp.ok) return false;
    const html = await resp.text();
    const live = html.match(/assets\/index-([A-Za-z0-9_-]+)\.js/)?.[1];
    if (!live || live === current) return false;
    console.log(`[main] new client version live (${current} → ${live}); reloading`);
    location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Resume flow: show loading, reset the state machine (so the incoming update
 * triggers a fresh onEnter), request a full state from the server, wait for
 * the state machine to finish processing it, then dismiss loading. No
 * animations — the goal is to snap the UI to current reality, not replay.
 */
async function refreshGameStateFromServer(): Promise<void> {
  if (resumeRefreshInFlight) return;
  if (!networkManager || !gameFlowController) return;
  resumeRefreshInFlight = true;

  try {
    loadingScreen.show();
    // Reconnect the game channel before emitting anything — after a long
    // background the socket is dead, and state:request into a dead socket
    // just hangs. ensureGameConnected forces the reconnect (and re-join).
    await networkManager.ensureGameConnected();
    // Flip active=true now that we're connected, so the server's state reply
    // reflects us being back in the round.
    networkManager.sendActiveStatus(true);
    gameFlowController.resetStateMachine();
    networkManager.sendStateRequest();

    // Wait for the state machine to process the incoming state:full — same
    // barrier used on initial game entry. Fail-safe timeout if the server
    // never answers (socket dead, etc.).
    await Promise.race([
      gameFlowController.getFirstUpdateProcessed(),
      new Promise<void>((resolve) => setTimeout(resolve, 8000)),
    ]);
  } finally {
    await loadingScreen.hide();
    resumeRefreshInFlight = false;
  }
}

/**
 * Setup game state event handlers
 */
function setupGameStateHandlers(): void {
  if (!gameStateManager) return;

  // When game starts - setup scene, then signal startGame() when done
  gameStateManager.on('game:started', async (data) => {
    const { config } = data as { config: { mode: string; gameType?: 'ceelo' | 'craps' } };
    inGame = true;

    // Onboarding "play a round" tasks — real multiplayer tables only, not the
    // isolated solo/tutorial scene.
    if (config.mode !== 'solo') {
      reportTutorial2Task(config.gameType === 'craps' ? 'playCraps' : 'playCeelo');
    }

    // Snapshot balance for net-win detection + bump the rating engagement count.
    preGameStacks = localBalance.stacks;
    try {
      const n = Number(localStorage.getItem(RATE_GAMES_KEY) || '0') + 1;
      localStorage.setItem(RATE_GAMES_KEY, String(n));
    } catch { /* private mode / quota — rating gate just stays conservative */ }

    // Pick the scene's GameModeId. Solo practice uses its own scene id;
    // matchmaking/friends use the actual gameType chosen at match time.
    const gameModeId: import('@sdl/scene-format').GameModeId =
      config.mode === 'solo'
        ? 'solo'
        : config.gameType === 'craps' ? 'craps' : 'ceelo';

    // Load currency note definitions from server (for bill textures/denominations)
    const { fetchCurrencyNotes } = await import('./api/currencyNoteApi');
    await fetchCurrencyNotes().catch(() => {}); // Non-blocking, fallback to hardcoded

    await setupGameScene(gameModeId);

    // Scene + controllers ready → NOW connect to game server (UDG pattern).
    // state:full will be processed immediately by the state machine since
    // all controllers exist.
    // Reset the state machine so each game session gets a fresh
    // firstUpdateProcessed barrier — otherwise the promise from a previous
    // game is already resolved and the loading screen doesn't actually wait
    // for the new game's initial onEnter.
    gameFlowController?.resetStateMachine();
    networkManager?.connectToGameServer();

    // Wait for the state machine to FINISH processing the initial update
    // (state:full → onEnter → countdown / profiles rendered), not just the
    // event arrival. The TaskPool is async so event listeners fire before
    // processUpdate completes — if we resolve on event arrival the loading
    // screen can dismiss to an empty scene and players miss the countdown.
    await Promise.race([
      gameFlowController?.getFirstUpdateProcessed() ?? Promise.resolve(),
      new Promise<void>((resolve) => setTimeout(resolve, 8000)), // fail-safe
    ]);

    // Signal that scene is fully loaded and state applied
    sceneSetupResolve?.();
  });

  // When game ends
  gameStateManager.on('game:ended', () => {
    returnToHomeFromGame();
  });

}

/**
 * Tear down the live game scene and return to the home screen. Shared by the
 * server-driven `game:ended` path and the menu's "Exit Match" button.
 */
/** Wire the Solo "Tables" button once solo is live: show it and keep its badge
 *  in sync with the live room count. */
function showSoloTablesButton(): void {
  // Tapping the net ticker opens the session roll ledger.
  soloStacksCounter.show(() => soloRollHistoryOverlay.open());
  soloMultiplayerButton.show(() => openSoloTablesOverlay());
  const update = (): void => {
    const rooms = lobbyService?.listRooms() ?? [];
    soloMultiplayerButton.setRoomCount(rooms.length);
    soloTablesOverlay.setRooms(rooms);
  };
  update();
  lobbyService?.on('rooms:updated', update);
  soloTablesUnbind = () => lobbyService?.off('rooms:updated', update);
}

/** Open the in-solo tables panel with the current live rooms. Reuses the lobby
 *  RoomList (game logos + avatar stacks) via the shared getPlayer lookup. */
function openSoloTablesOverlay(): void {
  soloTablesOverlay.show({
    rooms: lobbyService?.listRooms() ?? [],
    getPlayer: (id) => lobbyService?.getPlayer(id),
    onJoin: (roomId) => joinRoomFromSolo(roomId),
    onClose: () => soloTablesOverlay.hide(),
  });
}

/** Tap JOIN on a table: tear solo down, then join the chosen room. The pending
 *  id is fired at the end of returnToHomeFromGame (post-teardown). */
function joinRoomFromSolo(roomId: string): void {
  pendingJoinRoomId = roomId;
  postGameNavTarget = 'lobby'; // fallback screen if the join fails
  soloTablesOverlay.hide();
  exitMatchToHome();
}

function returnToHomeFromGame(): void {
  inGame = false;
  activeGameType = null;

  // Solo "Tables" button + overlay + counter teardown. Suppression is a
  // per-session flag owned by SoloWaitingState; reset it here so leaving from
  // the game-select modal (whose onExit never runs) can't leak a hidden pill
  // into the next session (e.g. a resume straight into BETTING).
  soloMultiplayerButton.setSuppressed(false);
  soloStacksCounter.setSuppressed(false);
  soloMultiplayerButton.hide();
  soloTablesOverlay.hide();
  soloStacksCounter.hide();
  soloRollHistoryOverlay.close();
  soloRollHistoryOverlay.clear();
  soloTablesUnbind?.();
  soloTablesUnbind = null;
  const navTarget = postGameNavTarget;
  postGameNavTarget = 'home';
  // Leaving solo to HOME drops the lobby sub we kept alive for the button;
  // going to LOBBY, the navigateTo below reconnects it.
  if (navTarget === 'home' && !pendingJoinRoomId) lobbyService?.disconnect();

  // Close the manual if it was left open in-game.
  manualOverlay?.close();

  // Hide game container (so 3D scene doesn't show through UI)
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) {
    gameContainer.style.visibility = 'hidden';
  }

  // Show main UI
  uiManager?.show();
  uiManager?.navigateTo(navTarget);

  // Clear game scene elements
  cleanupGameScene();

  // Solo → table: with solo fully torn down, fire the pending join. The lobby
  // browser (navTarget) sits underneath as the fallback if the join fails.
  if (pendingJoinRoomId) {
    const roomId = pendingJoinRoomId;
    pendingJoinRoomId = null;
    loadingScreen.show();
    networkManager?.joinLobbyRoom({ roomId });
    return;
  }

  // Recap net winnings on the home pill (navigateTo above no-ops when already
  // on 'home', so screen:change won't fire — trigger the check directly). Then,
  // once the money animation has landed, ride that winning beat into the rating
  // ask (self-gates on net-win + eligibility, mirroring the notification primer).
  setTimeout(() => {
    void (async () => {
      await checkHomeBalanceGain();
      await maybeAskForRating();
    })();
  }, 600);
}

/**
 * Menu "Exit Match" → deliberate leave. Tells the server to clean-remove +
 * settle our seat (NOT the keep-seat/reconnect path), drops the saved
 * reconnect data so we don't auto-rejoin on next launch, then runs the same
 * teardown-to-home as a natural game end. We don't wait for a server echo —
 * the player asked to leave, so we leave immediately.
 */
/**
 * Sign the player out: best-effort, time-boxed cleanup then a hard reload.
 * Shared by the Settings "Log out" action and the ban screen's "Sign out".
 * Every step is capped so a hung native call can't strand the user.
 */
async function performLogout(): Promise<void> {
  const capped = (p: Promise<unknown>, ms: number): Promise<unknown> =>
    Promise.race([Promise.resolve(p).catch(() => undefined), new Promise((r) => setTimeout(r, ms))]);
  await capped(notificationService.clearOnLogout(), 1500);
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sdl_') || key.startsWith('sdl-')) localStorage.removeItem(key);
    }
  } catch { /* private-mode / quota — nothing persisted anyway */ }
  await capped(googleSignOut(), 2000);
  window.location.reload();
}

/** Lock the app down to the ban screen: leave any game, go home, cover the UI. */
function enterBannedMode(): void {
  if (bannedOverlay?.isVisible()) return;
  try { exitMatchToHome(); } catch { /* may not be in a game */ }
  uiManager?.navigateTo('home');
  bannedOverlay?.show();
}

/** Lift the ban lock (live unban). */
function exitBannedMode(): void {
  bannedOverlay?.hide();
}

function exitMatchToHome(): void {
  voiceClient?.leave();
  networkManager?.leaveRoom();
  try {
    localStorage.removeItem('sdl_reconnect_token');
    localStorage.removeItem('sdl_room_id');
  } catch { /* private-mode / quota — non-fatal */ }
  // End via the state manager so engine.stop() runs — otherwise the GameLoop
  // keeps stepping physics and re-rendering the emptied scene into the hidden
  // game-container every frame, stealing the shared canvas from any UI view
  // (e.g. the bonus suitcase) and burning battery on the home screen.
  // endGame() emits 'game:ended' → returnToHomeFromGame().
  if (gameStateManager && gameStateManager.getState() !== 'menu') {
    gameStateManager.endGame();
  } else {
    returnToHomeFromGame();
  }
}

/**
 * Setup the game scene with enhanced visuals
 */
async function setupGameScene(gameModeId?: import('@sdl/scene-format').GameModeId): Promise<void> {
  if (!engine) return;

  // Reuse the resident environment (built once, kept warm by SceneManager). On
  // the first game it builds; on every subsequent game / try / tutorial with the
  // same scene id it returns the already-loaded scene with zero GPU churn.
  gameScene = await SceneManager.instance.ensureScene(gameModeId ?? 'craps');

  // The served scene document authors its lights at shadowMapSize 2048. Re-apply
  // the current quality tier now that the lights exist so the shadow map matches
  // the tier (high = 1024, à la Phone Dice) instead of the document's 2048.
  // This is also the only path that applies the tier's shadow size to scene
  // lights — SharedRenderer.setQuality only touches pixel ratio.
  engine.setQuality(settingsStore.get('graphics').quality);

  // Initialize dice controller (settings match prototype exactly)
  diceController = new DiceController(engine, {
    diceCount: 3,
    energyGain: 0.3,      // Energy gain per pixel of movement
    maxEnergy: 100,
    minEnergyToThrow: 18, // ~60px of motion; below it (or MIN_THROW_PATH) the dice drop for re-pickup
    maxPower: 35,         // Throw power (prototype: 35)
    baseForward: 5,       // Base forward force (prototype: 5)
    holdHeight: 4,        // Hold height (prototype: 4)
    pickupRadius: 1.5,    // Pickup radius
  });

  // Wire up dice asset manager for custom dice skins
  let equippedDiceIds: string[] = [];
  if (diceAssetManager) {
    diceController.setAssetManager(diceAssetManager);

    // Get equipped dice from the active preset, guarding an EMPTY preset
    // (fallback: last loaded → preset 0 → first owned) so we never enter a
    // game with the red procedural cubes.
    const equippedSlots = diceScreen?.resolveLoadoutSlots();
    if (equippedSlots) {
      equippedDiceIds = equippedSlots.filter(Boolean) as string[];
      diceScreen?.markLoadoutLoaded(equippedSlots);
      diceController.setEquippedDice(equippedDiceIds);

      // Ensure assets are loaded before creating dice (with 5s timeout). Shader
      // prewarm happens AFTER setAnchoredLights below (the light count must be
      // final first — see the prewarm call there).
      if (equippedDiceIds.length > 0) {
        await Promise.race([
          diceAssetManager.preload(equippedDiceIds),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      }
    }
  }

  // Dice creation is now owned by the state machine (WaitingStateHandler.onEnter
  // creates practice dice, HI_LO/roll handlers create game dice). No manual
  // creation here — it would bypass the state machine and leave stale dice
  // when transitioning to STARTING or HI_LO.

  // The shared, session-wide camera controller (one engine.camera) owned by
  // SceneManager. Reused across game / try-dice / tutorial; reset() returns it
  // to neutral on each exit. The game additionally drives betting anchors on it
  // via gameFlowController.setCameraAnchors below.
  cameraController = SceneManager.instance.cameraController;

  gameFlowController?.setEngine(engine);
  gameFlowController?.setDiceAssetManager(diceAssetManager);
  gameFlowController?.setCdnBase(diceCdnBase);
  gameFlowController?.setDiceController(diceController);
  gameFlowController?.setCameraController(cameraController);
  gameFlowController?.setCameraAnchors(gameScene.getCameraAnchors());
  gameFlowController?.setAnchoredLights(gameScene.getAnchoredLights());
  gameFlowController?.setPlayerProfilesOverlay(playerProfilesOverlay);
  gameFlowController?.setCountdownOverlay(countdownOverlay);

  // Compile the local player's dice shaders NOW, while the loading screen is up.
  // MUST run AFTER setAnchoredLights: that snaps the scene to its final shooting
  // light config, and since lights now keep a CONSTANT count across phases (see
  // GameFlowController.applyLightAnchor), the program warmed here is the exact
  // one used in BOTH shooting and betting — no mid-game recompile on phase change.
  // The state machine creates the real dice on WAITING enter and the player
  // throws within seconds; warming here (programs dedupe by cache key) means the
  // first roll never triggers a GLSL compile (a GPU-memory spike that can lose
  // the WebGL context on WKWebView).
  if (diceAssetManager && equippedDiceIds.length > 0) {
    await diceAssetManager.prewarmDiceShaders(equippedDiceIds, engine.camera, engine.scene);
  }

  if (!gameNetworkHandlersBound && networkManager) {
    gameNetworkHandlersBound = true;
    // Camera dice ownership lives entirely in GameFlowController / state machine
    // (driven by game:update). No game:state listener here — it clobbered the
    // spectator's opponent dice after handleNewRollResult set zoom mode.
  }

  // Initialize betting system
  const qualitySettings = engine.renderer.getBettingQualitySettings();
  bettingAssetLoader = new AssetLoader({
    quality: qualitySettings.walletModel as 'low' | 'medium' | 'high',
    basePath: '/assets',
  });
  bettingController = new BettingController(engine, bettingAssetLoader, {
    initialBalance: 10000,
    minBet: 100,
    maxBet: 1000000,
    swipeThreshold: 30,
    uiContainer: document.getElementById('ui-container') ?? undefined,
    // Apply the render quality tier to bills (segments / shadows / texture size)
    // so Low/Medium actually lighten the betting scene.
    billSegments: qualitySettings.billSegments,
    billShadows: qualitySettings.billShadows,
    billTextureSize: qualitySettings.billTextureSize,
  });

  try {
    await bettingController.initialize();
    // Compile the wallet's shaders during load (same WKWebView reason as dice):
    // the wallet's first reveal is a mid-game betting-entry beat, so a GLSL
    // compile there is a context-loss risk. Warm it now, then run the existing
    // visibility prewarm.
    await prewarmShaders(bettingController.getWalletGroup(), engine.camera, engine.scene);
    await prewarmObjectVisibility(engine, bettingController.getWalletGroup(), 1, {
      hideOffscreen: true,
    });
  } catch (error) {
    console.error('Failed to initialize betting controller', error);
  }

  bettingHUD3D = new BettingHUD3D({
    scene: engine.scene,
    camera: engine.camera,
    assetLoader: bettingAssetLoader ?? undefined,
  });
  bettingHUD3D.attachInput(engine.canvas);

  // Create DOM sidebet buttons overlay (positioned relative to wallet)
  const uiCont = document.getElementById('ui-container');
  if (uiCont) {
    sidebetButtonsOverlay = new SidebetButtonsOverlay({
      container: uiCont,
      camera: engine.camera,
      canvas: engine.canvas,
    });
    cancelBetOverlay = new CancelBetOverlay({
      container: uiCont,
      camera: engine.camera,
      canvas: engine.canvas,
    });
    // Per-zone cancel buttons on the local player's PLACED, still-unfaded
    // sidebets (server-refunded), distinct from the pendingCreate cancel above.
    sidebetCancelButtons = new SidebetCancelButtons({
      container: uiCont,
      camera: engine.camera,
      canvas: engine.canvas,
    });
    instructionBanner = new InstructionBanner({ container: uiCont });
  }

  gameFlowController?.setCancelBetOverlay(cancelBetOverlay);
  bettingController.setSidebetCancelButtons(sidebetCancelButtons);
  gameFlowController?.setInstructionBanner(instructionBanner);
  gameFlowController?.setBettingController(bettingController);
  gameFlowController?.setBettingHUD3D(bettingHUD3D);
  gameFlowController?.setSidebetButtonsOverlay(sidebetButtonsOverlay);

  // Wallet tutorial label is owned by the Wallet itself (show on open-complete,
  // hide on close-start). We only feed it the two gesture flags here.
  bettingController.on('betting:bill_placed', () => {
    bettingController?.getWallet().getInstructionsLabel()?.markTapUsed();
  });
  bettingController.on('betting:denomination_changed', () => {
    bettingController?.getWallet().getInstructionsLabel()?.markHoldUsed();
  });

  bettingController.on('betting:banker_pot_requested', (data) => {
    networkManager?.sendBet(data.amount, data.denominations);
    // Hold the local balance preview across the commit→server-deduct round trip.
    const lid = networkManager?.getPlayerId();
    if (lid) playerProfilesOverlay?.commitLocalPendingBet(lid, data.amount);
  });

  bettingController.on('betting:zone_fade_requested', (data) => {
    networkManager?.sendBetZoneFade(data.requestId, data.zoneId, data.amount, data.denominations);
    reportTutorial2Task('placeSidebet');
    const lid = networkManager?.getPlayerId();
    if (lid) playerProfilesOverlay?.commitLocalPendingBet(lid, data.amount);
  });

  bettingController.on('betting:zone_cancel_requested', (data) => {
    networkManager?.sendBetZoneCancel(data.zoneId);
  });

  bettingController.on('betting:zone_create_requested', (data) => {
    const betTypeMap: Record<string, string> = {
      // Cee-lo
      '4+': 'four_up',
      '3-': 'three_down',
      'BANK WINS': 'bank_wins',
      'BANK LOSES': 'bank_loses',
      // Craps — pre-come-out turn-level
      'SW': 'shooter_wins',
      'SL': 'shooter_loses',
      // Craps — mid-point multi-roll point pairs
      '4/10': 'point_pair_4_10',
      '5/9': 'point_pair_5_9',
      '6/8': 'point_pair_6_8',
      // Solo — Hi-Lo
      'LO': 'lo',
      '7': 'seven',
      'HI': 'hi',
      // Solo — Chuck-a-Luck faces
      '1': 'face_1',
      '2': 'face_2',
      '3': 'face_3',
      '4': 'face_4',
      '5': 'face_5',
      '6': 'face_6',
    };
    const betType = betTypeMap[data.betType] ?? data.betType.toLowerCase();
    networkManager?.sendBetZoneCreate(data.requestId, data.amount, betType, gameFlowController?.getBankerId() ?? undefined, data.denominations);
    reportTutorial2Task('placeSidebet');
  });

  // Register update callback
  engine.onUpdate((dt) => {
    diceController?.update(dt);
    bettingController?.update(dt);
    bettingHUD3D?.update(dt);
    sidebetButtonsOverlay?.update();
    cancelBetOverlay?.update();
    cameraController?.update(dt);
    // gameScene.update is an empty no-op (static scene) — not called.
    gameFlowController?.update(dt);

    // Physics debug visualization
    if (physicsDebugEnabled && engine) {
      updatePhysicsDebug(engine.physics, engine.scene);
    }
  });

  // When dice stop, zoom in and prompt player to pick them up
  diceController.on('dice:stopped', () => {
    // Cee-lo lag-trace marker (no-op for craps).
    gameFlowController?.markDiceStopped();
    // Zoom in on stopped dice
    cameraController?.setMode('zoom');
  });

  // State change handlers for dice controller events
  diceController.on('dice:pickup', () => {
    const heldCount = diceController?.collected ?? 0;
    const totalDice = diceController?.getDice().length ?? 0;
    if (heldCount < totalDice) {
      // Still collecting - zoom on remaining unheld dice
      cameraController?.setMode('zoom_unheld');
    } else {
    }
  });

  diceController.on('dice:all_collected', () => {
    // All dice collected - return to default camera (shoot position)
    cameraController?.setMode('default');
  });

  diceController.on('dice:rolling', () => {
    // Follow dice while rolling
    cameraController?.setMode('follow');
  });

  diceController.on('dice:throw', () => {
    // Start following dice when thrown
    cameraController?.setMode('follow');
  });
}

/**
 * Push the active loadout to the live game (in-game dice swap). No-op outside
 * a game (sendChangeEquippedDice has no gameSocket; diceController is null).
 * The server rebroadcasts game:update so every client diffs + animates the
 * slot; here we also prime the local 3D path so the next throw uses the new
 * skins right away.
 */
function sendLiveEquippedDice(preset: number): void {
  if (!diceScreen) return;
  // Guard an empty preset (fallback: last loaded → preset 0 → first owned) so
  // a live swap never broadcasts an empty loadout that would render red.
  const slots = diceScreen.resolveLoadoutSlots(preset);
  diceScreen.markLoadoutLoaded(slots);
  networkManager?.sendChangeEquippedDice(slots);
  const filtered = slots.filter(Boolean) as string[];
  diceController?.setEquippedDice(filtered);
  // Preload the new skins, then rebuild any at-rest local dice so the swap
  // shows on the felt now (not just next roll) and never falls back to the red
  // procedural cube because the asset wasn't ready at createDice time.
  if (filtered.length) {
    diceAssetManager?.preload(filtered)
      .then(() => {
        // Rebuild at-rest local dice with the new skin; if it recreated them,
        // hand the fresh array to the camera (it tracks dice by reference, and
        // createDice reassigns the array — otherwise the camera follows the old
        // disposed dice).
        if (diceController?.refreshEquippedAtRest()) {
          cameraController?.setDice(diceController.getDice());
        }
      })
      .catch(() => {});
  }
}

/**
 * Cleanup game scene when exiting
 */
function cleanupGameScene(): void {
  // Return the borrowed DiceScreen content to the lobby Dice tab if the
  // floating library was left open on exit (else the lobby tab is empty).
  floatingDiceLibrary?.forceRestore();

  if (diceController) {
    diceController.dispose();
    diceController = null;
  }

  if (bettingController) {
    bettingController.dispose();
    bettingController = null;
  }

  if (bettingHUD3D) {
    bettingHUD3D.dispose();
    bettingHUD3D = null;
  }

  // Reset, don't destroy: the overlay is created once in main() and the home
  // screen's cash flyouts (daily bonus, connect bonus) reuse it after the
  // game. Destroying it here left presentBalanceGain's overlay null → the
  // notes-to-pill flyout silently degraded to a bare count-up, and the next
  // game got setPlayerProfilesOverlay(null).
  playerProfilesOverlay?.resetForGameExit();

  if (sidebetButtonsOverlay) {
    sidebetButtonsOverlay.destroy();
    sidebetButtonsOverlay = null;
  }

  if (cancelBetOverlay) {
    cancelBetOverlay.destroy();
    cancelBetOverlay = null;
  }

  if (sidebetCancelButtons) {
    sidebetCancelButtons.destroy();
    sidebetCancelButtons = null;
  }

  if (instructionBanner) {
    instructionBanner.destroy();
    instructionBanner = null;
  }

  // The in-game menu is a global singleton (created once, reused every match).
  // Don't destroy it — just make sure it's closed so it isn't left showing when
  // we return to the home screen.
  inGameMenu?.close();

  if (cameraController) {
    cameraController.reset();
    cameraController = null;
  }

  gameFlowController?.setDiceController(null);
  gameFlowController?.setCameraController(null);
  gameFlowController?.setBettingController(null);
  gameFlowController?.setBettingHUD3D(null);
  gameFlowController?.setPlayerProfilesOverlay(null);
  gameFlowController?.setSidebetButtonsOverlay(null);
  gameFlowController?.setCancelBetOverlay(null);
  gameFlowController?.setInstructionBanner(null);

  // Reset, don't destroy — the overlay is created ONCE in main() alongside the
  // profiles overlay; nothing recreates it per game (unlike the instruction
  // banner). Destroying it here left every later game of the session with a
  // null overlay, so the "GAME STARTS IN" countdown silently never showed
  // again. stop() covers the leave-mid-countdown case the destroy was for: it
  // fades out to display:none, so no visible node strands under the home
  // screen. setupGameScene re-wires the live ref on the next game.
  countdownOverlay?.stop();
  gameFlowController?.setCountdownOverlay(null);

  // Drop our reference only — the environment (scene + lights + static bodies)
  // is owned by SceneManager and stays resident for the next game / try-dice /
  // tutorial. Disposing it or wiping physics here is exactly the churn that
  // black-rendered iOS.
  gameScene = null;

  // Remove any leftover ACTOR objects (controllers should remove their own, but
  // this is the safety net the old blanket clear provided) WITHOUT touching the
  // resident environment. Static physics bodies stay; dice bodies were already
  // removed by diceController.dispose() above.
  if (engine) {
    const scene = engine.scene;
    const keep = new Set(SceneManager.instance.current?.getPersistentObjects() ?? []);
    for (const child of [...scene.children]) {
      if (!keep.has(child)) scene.remove(child);
    }
  }
}

/**
 * Handle actions from the home screen
 */
function handleHomeAction(action: string): void {

  switch (action) {
    case 'play':
      // HIT THE BLOCK opens the lobby. Matchmaking will be triggered from
      // inside the lobby (Quick Match / Join Room) once those are wired.
      uiManager?.navigateTo('lobby');
      break;
    case 'solo':
      // Solo Grind: allocate a single-player house-banked room and drop straight
      // in. Server replies via match:found → startGame (same path as a table).
      // NOTE: keep locked on prod (re-lock this before any deploy:web) until the
      // game-server + main-server solo changes ship to prod.
      if (!networkManager) { showComingSoonModal(action); break; }
      loadingScreen.show();
      networkManager.findSoloGame();
      break;
    case 'friends':
    case 'leaderboard':
    case 'crew-wars':
      // These modes are locked for the multiplayer beta.
      showComingSoonModal(action);
      break;
    case 'daily-bonus':
      if (pendingBonusPrize != null) {
        openDailyBonus();
      } else {
        homeScreen?.showCooldownTooltip();
      }
      break;
    case 'notifications':
      if (notificationsScreen) {
        uiManager?.openModal('notifications', notificationsScreen.element);
        notificationsScreen.onOpen();
      }
      break;
    case 'stacks':
    case 'gold':
      // Navigate to shop
      uiManager?.navigateTo('shop');
      break;
    case 'profile':
      // Tap the home avatar → Settings → Account (to change the photo).
      settingsScreen?.openTab('account');
      uiManager?.navigateTo('settings');
      break;
  }
}

// ─── Tutorial Part 2 task board ──────────────────────────────────────────────

/** Whether the board should be visible: finished part 1 by playing (not Skip)
 *  and part 2 not yet complete. Skip → no board, already eligible. */
function tutorial2Eligible(): boolean {
  return !!(localTutorial?.completed && !localTutorial.skipped) && !localTutorial2?.completed;
}

/** Report a task to the server (idempotent there; gated here to avoid traffic). */
function reportTutorial2Task(task: Tutorial2TaskId): void {
  if (!localTutorial2 || localTutorial2.completed || localTutorial2[task]) return;
  if (!tutorial2Eligible()) return;
  networkManager?.sendTutorial2Task(task);
}

/** Re-render + show/hide the board from current state, and auto-complete tasks
 *  the player already satisfied before the board existed (so they don't stall). */
function updateTaskBoard(): void {
  if (!taskBoard || !localTutorial2) return;
  if (!tutorial2Eligible()) { taskBoard.setVisible(false); return; }
  taskBoard.applyState(localTutorial2);
  taskBoard.setVisible(true);
  if (!localTutorial2.connectAccount && localLinked) reportTutorial2Task('connectAccount');
  if (!localTutorial2.setPfp && !!localPicture) reportTutorial2Task('setPfp');
}

/** Reveal a freshly-granted page die via the shared reward modal. */
async function revealPageDie(page: 1 | 2, diceId: string): Promise<void> {
  if (!diceCdnBase) return;
  if (!storeRewardModal) storeRewardModal = new TutorialRewardModal();
  const reward = storeRewardModal;
  reward.show({
    heading: 'NEW DICE UNLOCKED',
    message: page === 1 ? 'Page one done. Keep it rolling.' : 'Both pages done. You are all set.',
    dieThumbnailUrl: `${diceCdnBase}/dice/${diceId}/thumb-128.png`,
    buttonLabel: 'COLLECT',
  });
  await new Promise<void>((resolve) => reward.once('continue', resolve));
}

/** After all queued reveals have played: retire the board if both pages are
 *  done, otherwise refresh it to the next page. */
function afterTutorial2Reveals(): void {
  if (localTutorial2?.completed && taskBoard) taskBoard.retire();
  else updateTaskBoard();
  // The daily-challenge gate opens on tutorial2 completion — the server
  // pushed the first state alongside TUTORIAL2_UPDATED, so the widget takes
  // the retiring board's place immediately.
  updateDailyChallengesWidget();
}

// ─── Daily challenges ────────────────────────────────────────────────────────

/** Show/hide + re-render the widget from server state. Visibility is the
 *  server's `unlocked` gate verbatim — no parallel client flags to drift. */
function updateDailyChallengesWidget(): void {
  if (!dailyChallengesWidget) return;
  if (!localDailyChallenges?.unlocked) {
    dailyChallengesWidget.setVisible(false);
    return;
  }
  dailyChallengesWidget.applyState(localDailyChallenges);
  dailyChallengesWidget.setVisible(true);
}

/** Play any queued page-die reveals, but only on the home screen and out of a
 *  match (so the reveal doesn't fire over the table). */
function flushTutorial2Reveals(): void {
  if (!pendingTutorial2Reveals.length) return;
  if (uiManager?.getCurrentScreen() !== 'home' || inGame) return;
  const next = pendingTutorial2Reveals.shift()!;
  void revealPageDie(next.page, next.diceId).then(() => {
    if (pendingTutorial2Reveals.length) flushTutorial2Reveals();
    else afterTutorial2Reveals();
  });
}

/** Route a task row tap to its screen. */
function routeTaskTarget(target: TaskTarget): void {
  switch (target) {
    case 'daily-bonus': handleHomeAction('daily-bonus'); break;
    case 'play': handleHomeAction('play'); break;
    case 'account': handleHomeAction('profile'); break;
    case 'crew': uiManager?.navigateTo('crew'); break;
    case 'dice': uiManager?.navigateTo('dice'); break;
  }
}

/**
 * Open the in-game manual / playbook, optionally jumping to a section. Created
 * lazily on first open and reused thereafter (appended to document.body so its
 * fixed positioning pins to the viewport regardless of any transformed UI
 * container ancestor).
 */
function openManual(section: ManualSectionId = 'lobby'): void {
  if (!manualOverlay) manualOverlay = new ManualOverlay();
  manualOverlay.open(section);
  networkManager?.reportManualOpened(section);
}

/**
 * Per-mode copy for the "locked during the multiplayer beta" modal. Accent +
 * icon mirror the home card so the modal reads as the same mode.
 */
const COMING_SOON_MODES: Record<string, Omit<ComingSoonOptions, 'onPlayLive'>> = {
  solo: {
    title: 'SOLO GRIND',
    iconSrc: '/assets/icons/modes/solo.svg',
    accent: '#00F5FF',
    message: "Practice your throw and stack bread on your own time. Solo Grind opens up after the multiplayer beta.",
  },
  friends: {
    title: 'RUN IT UP',
    iconSrc: '/assets/icons/modes/friends.svg',
    accent: '#FF0080',
    message: "Spin up a private table and bring your crew along. Run It Up opens up after the multiplayer beta.",
  },
  leaderboard: {
    title: 'LEADERBOARD',
    iconSrc: '/assets/icons/modes/leaderboard.svg',
    accent: '#FFD700',
    message: "See who's really running the streets. Rankings go live after the multiplayer beta.",
  },
  'crew-wars': {
    title: 'CREW WARS',
    iconSrc: '/assets/icons/modes/crew-wars.svg',
    accent: '#A855F7',
    message: "Rep your set against rival crews for turf and bragging rights. Crew Wars drops after the multiplayer beta.",
  },
};

let comingSoonOverlay: ComingSoonOverlay | null = null;

/**
 * Explain why a home mode is locked for the beta and funnel the player toward
 * the live tables (HIT THE BLOCK opens the lobby).
 */
function showComingSoonModal(mode: string): void {
  const def = COMING_SOON_MODES[mode];
  if (!def) return;
  if (!comingSoonOverlay) comingSoonOverlay = new ComingSoonOverlay();
  void comingSoonOverlay.show({
    ...def,
    onPlayLive: () => uiManager?.navigateTo('lobby'),
  });
}

/**
 * Compute suitcase cash stack count from prize amount (log-scaled 4-24).
 */
function getCashStackCount(prize: number): number {
  const MIN = 1_200_000;
  const MAX = 1_200_000_000;
  const logMin = Math.log(MIN);
  const logMax = Math.log(MAX);
  const logP = Math.log(Math.max(MIN, Math.min(MAX, prize)));
  const t = (logP - logMin) / (logMax - logMin);
  return Math.round(4 + t * 20);
}

/**
 * Open the daily bonus suitcase experience
 */
function openDailyBonus(): void {
  if (bonusSuitcaseOverlay) return; // Already open
  dismissDailyBonusNudge(); // they found it — clear the post-tutorial coach mark
  if (pendingBonusPrize == null) return; // No bonus available from server

  const uiContainer = document.getElementById('ui-container');
  if (!uiContainer) return;

  const prize = pendingBonusPrize;
  const stackCount = getCashStackCount(prize);
  const streakMult = pendingBonusMultiplier;

  let committed = false;

  bonusSuitcaseOverlay = new BonusSuitcaseOverlay(uiContainer);
  bonusSuitcaseOverlay.open(prize, stackCount, streakMult);

  // Consume the bonus ONLY when the case actually opens — anti-abuse (no
  // close-and-retry to hunt a bigger bonus; the amount is final once the case
  // opens), and merely peeking then leaving keeps it claimable. We also set
  // bonusInFlight so the bonus's BALANCE_UPDATE doesn't fire an immediate
  // background home flyout — we play that explicitly on close instead.
  bonusSuitcaseOverlay.on('opened', () => {
    if (committed) return;
    committed = true;
    bonusInFlight = true;
    networkManager?.claimDailyBonus();
    pendingBonusPrize = null;
    homeScreen?.updatePlayerData({ hasDailyBonus: false });
  });

  bonusSuitcaseOverlay.on('close', async () => {
    bonusSuitcaseOverlay?.destroy();
    bonusSuitcaseOverlay = null;
    bonusInFlight = false; // release suppression; presentBalanceGain re-manages it
    // Notes fly from the (centre-screen) suitcase to the Stacks pill + count up.
    if (committed && pendingDailyAward) {
      const award = pendingDailyAward;
      pendingDailyAward = null;
      await presentBalanceGain(award.newBalance, { x: window.innerWidth / 2, y: window.innerHeight / 2 });
      // Money has landed on the home pill — NOW offer notifications, so the
      // primer reads as a calm follow-up on the main screen rather than fighting
      // the flyout. One-shot, gated (skips if already asked / not in prompt).
      void maybePrimeNotifications();
    }
  });
}

/** Cleanup for the post-tutorial daily-bonus nudge (tap + timeout listeners). */
let dailyBonusNudgeCleanup: (() => void) | null = null;

/**
 * One-time, post-tutorial nudge: spotlight the daily-bonus gift button so new
 * players learn where their free daily reward lives and start the streak on day
 * one. The player taps the gift themselves (we don't auto-open) so the location
 * sticks for tomorrow. Fires once ever, guarded by localStorage, and only when a
 * bonus is actually available on the home screen. The coach mark is
 * pointer-events:none, so the tap that dismisses it also reaches the gift.
 */
function showDailyBonusNudge(): void {
  if (dailyBonusCoach) return;
  if (localStorage.getItem('sdl_daily_nudge_seen')) return;
  if (uiManager?.getCurrentScreen() !== 'home') return;
  if (!homeScreen?.hasDailyBonusAvailable()) return;

  localStorage.setItem('sdl_daily_nudge_seen', '1');

  const coach = new CoachMarkOverlay({ showSkip: false, highlight: true });
  dailyBonusCoach = coach;
  coach.setStep({
    text: 'Your daily bonus is waiting!',
    subtext: 'Tap the gift to claim free Stacks... come back daily to grow your streak.',
    target: () => {
      const r = homeScreen?.getDailyBonusButtonRect();
      return r ? { x: r.left, y: r.top, width: r.width, height: r.height } : null;
    },
    bannerPosition: 'bottom',
    // Gift button hugs the top of the screen, so point up at it from below.
    arrowPosition: 'bottom',
    pad: 8,
  });
  coach.show();

  // Dismiss on the first tap anywhere (the gift tap opens the suitcase AND
  // clears this via openDailyBonus), or after a timeout if the player ignores it.
  const onTap = () => dismissDailyBonusNudge();
  window.addEventListener('pointerdown', onTap, true);
  const timer = window.setTimeout(() => dismissDailyBonusNudge(), 12000);
  dailyBonusNudgeCleanup = () => {
    window.removeEventListener('pointerdown', onTap, true);
    window.clearTimeout(timer);
  };
}

/** Tear down the post-tutorial daily-bonus nudge. Idempotent. */
function dismissDailyBonusNudge(): void {
  dailyBonusNudgeCleanup?.();
  dailyBonusNudgeCleanup = null;
  if (!dailyBonusCoach) return;
  dailyBonusCoach.destroy();
  dailyBonusCoach = null;
}

/**
 * First-login tutorial gate. Lazily builds the Welcome modal and wires its two
 * paths. Skip grants the full reward immediately (balance + gear refresh arrive
 * via the existing balance:update / player:gear_updated listeners). "LET'S ROLL"
 * launches the guided tutorial — wired in a later phase (D/E).
 */
function showWelcomeGate(): void {
  if (!welcomeModal) {
    welcomeModal = new WelcomeModal();
    welcomeModal.on('skip', () => {
      networkManager?.skipTutorial();
      triggerConnectBonus();
    });
    welcomeModal.on('start', () => {
      void startTutorial();
    });
  }
  welcomeModal.show();
}

/**
 * After the tutorial ends (and any time on home), surface the 100M connect
 * bonus. Linked + unclaimed → claim it (server-authoritative; the
 * 'account:connect_bonus_result' listener plays the cash beat). Guests → the
 * persistent "connect to claim 100M" CTA.
 *
 * NOTE: real Apple/Google sign-in is not yet built (LoginScreen buttons are
 * "coming soon"), so today the linked branch never fires at runtime and the
 * guest CTA is a TODO — the grant mechanism is ready for when sign-in lands.
 */
/**
 * Surface the 100M connect bonus and resolve once its cash flyout has finished.
 * Resolves IMMEDIATELY when there's no bonus (guest / already claimed), so
 * callers can simply `await triggerConnectBonus()` then run their follow-up UI
 * without racing timers. (Guest CTA is a TODO until sign-in lands.)
 */
function triggerConnectBonus(): Promise<void> {
  if (!(localLinked && !localConnectBonusClaimed)) return Promise.resolve();
  return new Promise<void>((resolve) => {
    resolveConnectBonusFlyout = resolve;
    networkManager?.claimConnectBonus();
  });
}

/** Resolve after the next paint (two rAFs) — wait out a layout/visibility flip
 *  without a magic timeout. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

// ── Notification soft-ask + deep-link routing ──────────────────────────────

/** Lazy primer modal. */
let notificationPrimer: NotificationPrimer | null = null;
const NOTIF_PRIMED_KEY = 'sdl_notif_primed';

/**
 * Show the soft-ask priming modal at a value moment (first DM, first bonus), but
 * only once and only when it can actually help: native platform, OS permission
 * still un-asked ('prompt'), and we haven't primed before. If the player taps
 * Enable we fire the real OS prompt; either way we mark it so we never nag.
 */
async function maybePrimeNotifications(): Promise<void> {
  try {
    if (localStorage.getItem(NOTIF_PRIMED_KEY)) return;
    const state = await notificationService.getPermission();
    if (state !== 'prompt') {
      // Already granted (token synced silently) or denied (prompt is spent) —
      // nothing the primer can do. Record it so we stop checking.
      localStorage.setItem(NOTIF_PRIMED_KEY, '1');
      return;
    }
    if (!notificationPrimer) notificationPrimer = new NotificationPrimer();
    const enable = await notificationPrimer.show();
    localStorage.setItem(NOTIF_PRIMED_KEY, '1'); // one-shot regardless of choice
    if (enable) {
      const result = await notificationService.requestPermissionAndRegister();
      if (result === 'denied') {
        showToast('You can turn notifications on later in Settings');
      }
    }
  } catch (err) {
    console.warn('[notif] prime failed:', err);
  }
}

// ── In-app store rating (sentiment funnel) ────────────────────────────────

/** Lazy sentiment-funnel primer. */
let ratingPrimer: RatingPrimer | null = null;
/** Balance at the start of the current game, to detect a net-winning session. */
let preGameStacks = 0;

const RATE_RATED_KEY = 'sdl_rate_rated';     // player went to the store; never re-ask
const RATE_OPTOUT_KEY = 'sdl_rate_optout';   // reserved for an explicit opt-out
const RATE_GAMES_KEY = 'sdl_rate_games';     // games played (engagement proxy)
const RATE_LAST_ASK_KEY = 'sdl_rate_last_ask'; // epoch ms of last primer shown
const RATE_FIRST_SEEN_KEY = 'sdl_rate_first_seen'; // epoch ms of first launch

const RATE_MIN_GAMES = 3;
const RATE_MIN_AGE_MS = 24 * 60 * 60 * 1000;       // 1 day since first launch
const RATE_COOLDOWN_MS = 45 * 24 * 60 * 60 * 1000; // re-ask no sooner than 45 days

/** Record first-launch time once, so the age gate accrues from install. */
function ensureRatingFirstSeen(): void {
  try {
    if (!localStorage.getItem(RATE_FIRST_SEEN_KEY)) {
      localStorage.setItem(RATE_FIRST_SEEN_KEY, String(Date.now()));
    }
  } catch { /* private mode / quota — fine, age gate just won't block */ }
}

/**
 * Ask for a store rating at a peak-positive moment (post-win home return). The
 * native sheet is silent + OS-quota-limited, so the only lever is WHO we ask:
 * happy winners only. A "LOVING IT" tap fires the native sheet; "NOT REALLY"
 * routes to the feedback form (keeping unhappy sentiment out of the store).
 *
 * All gates are local (sdl_rate_* in localStorage), mirroring the notification
 * primer. The funnel UI still runs in the dev browser; only the native review
 * call no-ops off-device.
 */
async function maybeAskForRating(): Promise<void> {
  try {
    // Winning session only — never ask after a loss.
    if (localBalance.stacks <= preGameStacks) return;
    // Already rated or opted out → never nag again.
    if (localStorage.getItem(RATE_RATED_KEY) || localStorage.getItem(RATE_OPTOUT_KEY)) return;
    // Don't collide with the notification primer in the same moment.
    if (document.querySelector('.sdl-nprime-host.visible')) return;
    // Engagement gate.
    const games = Number(localStorage.getItem(RATE_GAMES_KEY) || '0');
    if (games < RATE_MIN_GAMES) return;
    // Age gate — don't ask brand-new installs.
    const firstSeen = Number(localStorage.getItem(RATE_FIRST_SEEN_KEY) || '0');
    if (firstSeen && Date.now() - firstSeen < RATE_MIN_AGE_MS) return;
    // Cooldown — stricter than the OS quota so we never burn an ask on a weak moment.
    const lastAsk = Number(localStorage.getItem(RATE_LAST_ASK_KEY) || '0');
    if (lastAsk && Date.now() - lastAsk < RATE_COOLDOWN_MS) return;

    // Record the ask up front so a crash/close mid-primer still applies cooldown.
    localStorage.setItem(RATE_LAST_ASK_KEY, String(Date.now()));

    if (!ratingPrimer) ratingPrimer = new RatingPrimer();
    const choice = await ratingPrimer.show();
    if (choice === 'love') {
      // Assume they'll rate; the native sheet gives us no result, so don't re-ask.
      localStorage.setItem(RATE_RATED_KEY, '1');
      await ratingService.requestReview();
    } else if (choice === 'meh') {
      // Route unhappy sentiment to feedback, not the store.
      uiManager?.navigateTo('settings');
      settingsScreen?.openTab('support');
    }
    // 'later' → nothing now; the cooldown above re-asks down the line.
  } catch (err) {
    console.warn('[rate] ask failed:', err);
  }
}

/** Route a tapped push (streetdice://...) to the right in-app surface. */
function routePushDeepLink(url: string): void {
  try {
    // streetdice://chat/<id> | streetdice://bonus | streetdice://notifications
    const path = url.replace(/^streetdice:\/\//, '');
    const [head, arg] = path.split('/');
    if (head === 'chat' && arg) {
      uiManager?.navigateTo('crew');
      openStandaloneChat(arg);
    } else if (head === 'notifications') {
      if (notificationsScreen) {
        uiManager?.openModal('notifications', notificationsScreen.element);
        notificationsScreen.onOpen();
      }
    } else if (head === 'support') {
      uiManager?.navigateTo('settings');
      settingsScreen?.openTab('support');
    } else {
      // 'bonus' and anything else → home, where the bonus is claimable.
      uiManager?.navigateTo('home');
    }
  } catch (err) {
    console.warn('[notif] deep-link route failed:', url, err);
  }
}

/** Singleton cash-reward presenter (lazy). */
let cashRewardOverlay: CashRewardOverlay | null = null;
/** Multi-dice "NEW DICE UNLOCKED" reveal after an offer purchase. */
let diceUnlockedModal: DiceUnlockedModal | null = null;
/** True while a cash-award animation owns the home Stacks pill (suppresses the
 *  instant balance:update snap so the count-up reads). */
let bonusInFlight = false;

/**
 * Celebrate a balance gain, gated by `lastSeenBalance` so each gain animates
 * exactly once regardless of how many paths report it. Presentation is
 * context-aware:
 *  - On HOME: notes fly from `fromScreenPos` (or screen-centre) to the Stacks
 *    pill, above the home UI, and the pill counts up. No card.
 *  - Elsewhere (e.g. the Store): the player-card cash beat.
 * A non-positive delta just syncs the gate silently (losses don't animate).
 */
async function presentBalanceGain(newBalance: number, fromScreenPos?: { x: number; y: number }): Promise<void> {
  const amount = newBalance - lastSeenBalance;
  if (amount <= 0) {
    lastSeenBalance = newBalance;
    // Nothing to animate (a loss / no gain), but still make the pill truthful —
    // a suppressed (bonusInFlight) balance:update can leave it stale, and a
    // silent return here is what froze the home balance until restart.
    homeScreen?.updatePlayerData({ stacks: newBalance });
    return;
  }
  const startBalance = lastSeenBalance;
  lastSeenBalance = newBalance; // claim immediately — prevents a double-show

  const onHome = uiManager?.getCurrentScreen() === 'home';
  if (!onHome || !homeScreen) {
    await showCashReward(amount, startBalance);
    return;
  }

  const pill = homeScreen.getStacksPillRect();
  const overlay = playerProfilesOverlay;
  // Notes aren't loaded on home by default — fetch real bill art, fall back to
  // the Stacks icon so the flyout is never a silent no-op.
  await fetchCurrencyNotes().catch(() => { /* fall back to the icon */ });
  const noteUrl = getNoteCdnUrl(100_000_000) ?? getNoteCdnUrl(amount) ?? '/assets/icons/currency/stacks.svg';

  bonusInFlight = true;
  try {
    // Hold the pill at the pre-gain value during the flyout (overwrites any
    // balance:update snap before paint), then count up when the notes land.
    homeScreen.updatePlayerData({ stacks: startBalance });
    const countUp = () => homeScreen?.animateStacksIncrease(newBalance, 1200, startBalance);

    if (overlay && pill) {
      overlay.setFlyoutAboveUI(true);
      overlay.show();
      const target = { x: pill.left + pill.width / 2, y: pill.top + pill.height / 2 };
      const spawn = fromScreenPos ?? { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };
      try {
        // resolveOnComplete: the hide + z-index restore below run on resolve, so
        // we must wait out the +$X label's linger/float-up — otherwise the label
        // finishes its animation at z-index 45, behind the home UI.
        await overlay.flyoutCashWin('', amount, spawn, noteUrl, countUp, { targetOverride: target, skipBalanceCountUp: true, resolveOnComplete: true, labelLiftHeights: 1 });
      } finally {
        overlay.hide();
        overlay.setFlyoutAboveUI(false);
      }
    } else {
      countUp();
      await new Promise((r) => setTimeout(r, 1300));
    }
  } catch (err) {
    // The flyout is cosmetic; a failure must never strand bonusInFlight (which
    // would freeze the home Stacks pill on gold-only updates until an app
    // restart) or leave the pill showing the stale pre-gain value.
    console.warn('[balance] gain flyout failed; snapping pill to final balance', err);
  } finally {
    // Always land on the true balance and release the suppression flag.
    homeScreen?.updatePlayerData({ stacks: newBalance });
    bonusInFlight = false;
  }
}

/**
 * Fired whenever the home screen becomes visible (tab nav, game return). If the
 * balance grew while home was away (e.g. game winnings) and hasn't been shown,
 * present the gain on the pill. The gate makes already-shown gains a no-op.
 */
function checkHomeBalanceGain(): Promise<void> {
  if (uiManager?.getCurrentScreen() !== 'home') return Promise.resolve();
  // Reconcile whenever the pill and lastSeenBalance diverge — NOT just on gains.
  // A GAIN animates the flyout; a LOSS/equal snaps the pill and syncs
  // lastSeenBalance (presentBalanceGain's amount<=0 path). Syncing on losses is
  // the root fix: otherwise lastSeenBalance stays stale after a losing game, and
  // the next gain (e.g. a daily bonus) computes a negative amount, so the flyout
  // never fires and the balance freezes until an app restart.
  if (localBalance.stacks !== lastSeenBalance) {
    return presentBalanceGain(localBalance.stacks);
  }
  return Promise.resolve();
}

/**
 * Global cash-reward beat — player card drops down + in-game note flyout from
 * screen centre + balance count-up. Reusable anywhere (connect bonus, daily
 * bonus, gifts...). `startStacks` defaults to the current local balance; pass
 * the PRE-grant balance when the server has already credited the amount.
 */
async function showCashReward(
  amount: number,
  startStacks?: number,
  opts?: { zIndex?: number; aboveUI?: boolean },
): Promise<void> {
  if (!cashRewardOverlay) cashRewardOverlay = new CashRewardOverlay();
  // Load the real bill art first so we use the in-game cash flyout (with the
  // CASH_COUNT / CASH_LAND SFX) rather than the silent CSS-bill fallback. Mirrors
  // presentBalanceGain's home path; also fixes its off-home path (e.g. Store).
  await fetchCurrencyNotes().catch(() => { /* fall back to icon */ });
  const overlay = playerProfilesOverlay;
  const noteUrl = getNoteCdnUrl(100_000_000);
  const runFlyout = overlay && noteUrl
    ? async (target: { x: number; y: number }, onLastNoteFlightStart: () => void) => {
        // Overlay defaults to opacity:0; show it for the flyout (no cards set,
        // so only the notes render), then hide it again. aboveUI lifts it over
        // high-z surfaces (e.g. the offer popup) so the notes aren't occluded.
        if (opts?.aboveUI) overlay.setFlyoutAboveUI(true);
        overlay.show();
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        await overlay.flyoutCashWin('', amount, center, noteUrl, onLastNoteFlightStart, {
          targetOverride: target,
          skipBalanceCountUp: true,
          // Same label-vs-hide race as presentBalanceGain: hide() fades the
          // overlay on resolve, so wait for the label's full linger/float-up.
          resolveOnComplete: true,
        });
        overlay.hide();
        if (opts?.aboveUI) overlay.setFlyoutAboveUI(false);
      }
    : undefined;
  return cashRewardOverlay.show({
    username: localUsername,
    picture: localPicture,
    startStacks: startStacks ?? localBalance.stacks,
    amount,
    noteUrl,
    runFlyout,
    zIndex: opts?.zIndex,
  });
}

/**
 * Launch the guided tutorial in an ISOLATED scene (no game-server room). Hides
 * the home UI, shows the 3D view, and hands control to TutorialController. The
 * onExit callback restores the home UI and disposes the instance. Reward dice
 * codes mirror main-server config.game.tutorial.
 */
async function startTutorial(): Promise<void> {
  if (!engine || !networkManager || !uiManager || !gameFlowController) return;
  // Drop any prior instance (its coach/reward overlays live on document.body).
  tutorialController?.destroy();
  tutorialController = null;

  // Cover the scene build with the loading screen (the engine canvas is black
  // until the first frame renders).
  loadingScreen.show();
  uiManager.hide();
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) gameContainer.style.visibility = 'visible';

  // The first two loadout slots are the craps dice (slot 2 is cee-lo). Use the
  // player's equipped red default dice so the tutorial matches their inventory.
  const activePreset = diceScreen?.getActivePreset() ?? 0;
  const slots = diceScreen?.getPresetSlots(activePreset) ?? [];
  const equippedDice = [slots[0], slots[1]].filter(Boolean) as string[];

  tutorialController = new TutorialController({
    engine,
    networkManager,
    gameFlowController,
    playerProfilesOverlay,
    diceAssetManager,
    cdnBase: diceCdnBase,
    equippedDice,
    rewardDice: { shoot: 'ds01-66f7a44e', bet: 'dss01-004157a0' },
    onExit: async (reason) => {
      const gc = document.getElementById('game-container');
      if (gc) gc.style.visibility = 'hidden';
      uiManager?.show();
      tutorialController?.destroy();
      tutorialController = null;
      // Reflect the part-1 completion client-side so the part-2 onboarding board
      // appears this session (server already recorded it; auth state was stale).
      // A Skip stays ineligible for the board (and is already daily-challenge
      // eligible), matching the server's tutorial flags.
      localTutorial = { shootDone: true, betDone: true, completed: true, skipped: reason === 'skipped' };
      updateTaskBoard();
      // Surface the 100M connect bonus and AWAIT its cash flyout (resolves at
      // once if there's no bonus), then let home settle, then teach the daily
      // bonus. Linear awaits — the coach's highlight can never overlap or lag
      // against the flyout. The nudge is once-ever (sdl_daily_nudge_seen).
      await triggerConnectBonus();
      await nextPaint();
      showDailyBonusNudge();
    },
  });
  await tutorialController.start();
  await loadingScreen.hide();
}

/** Browser preview has no app store: tell the player purchases are off here, but
 *  let them jump straight into trying the dice (offers with dice). */
async function showBrowserPurchaseNotice(offer?: OfferWire): Promise<void> {
  if (offer?.dice?.length) {
    const tryIt = await confirmModal.show({
      title: 'Browser preview',
      message: 'Purchases are disabled in the browser. You can still try the dice.',
      confirmLabel: 'TRY DICE',
      cancelLabel: 'GOT IT',
    });
    if (tryIt) void startDiceTry(offer);
  } else {
    await confirmModal.show({
      title: 'Browser preview',
      message: 'Purchases are disabled in the browser.',
      confirmLabel: 'GOT IT',
      hideCancel: true,
    });
  }
}

/**
 * Launch the local "try the dice" demo for an offer. Mirrors startTutorial:
 * suspend the offer popup + home UI, take over the game container with an
 * isolated shoot scene, and restore everything on exit (re-showing the popup so
 * the buy flow stays intact).
 */
async function startDiceTry(offer: OfferWire): Promise<void> {
  if (!engine || !offer.dice?.length) return;
  offerTryController?.destroy();
  offerTryController = null;

  loadingScreen.show();
  offerPopup?.suspend();
  uiManager?.hide();
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) gameContainer.style.visibility = 'visible';

  offerTryController = new OfferDiceTryController({
    engine,
    diceAssetManager,
    cdnBase: diceCdnBase,
    offerDice: offer.dice,
    onExit: () => {
      const gc = document.getElementById('game-container');
      if (gc) gc.style.visibility = 'hidden';
      uiManager?.show();
      offerTryController?.destroy();
      offerTryController = null;
      // Back to the offer card so the player can still buy.
      offerPopup?.resume();
    },
  });
  await offerTryController.start();
  await loadingScreen.hide();
}

/**
 * Launch the local "try" demo for the player's EQUIPPED loadout (Dice screen TRY
 * button). Same isolated shoot scene as the offer demo, with a 2 / 3 dice toggle
 * (craps / cee-lo). Close (X) restores the home UI on the current Dice screen.
 */
async function startEquippedTry(slots: string[], onExitExtra?: () => void): Promise<void> {
  if (!engine || slots.length === 0) return;
  equippedTryController?.destroy();
  equippedTryController = null;

  loadingScreen.show();
  uiManager?.hide();
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) gameContainer.style.visibility = 'visible';

  equippedTryController = new EquippedDiceTryController({
    engine,
    diceAssetManager,
    cdnBase: diceCdnBase,
    slots,
    onExit: () => {
      const gc = document.getElementById('game-container');
      if (gc) gc.style.visibility = 'hidden';
      uiManager?.show();
      equippedTryController?.destroy();
      equippedTryController = null;
      onExitExtra?.();
    },
  });
  await equippedTryController.start();
  await loadingScreen.hide();
}

/** Map a room/match ErrorPayload to a short, player-facing line (no dashes). */
function friendlyMatchError(error: { code?: string; message?: string }): string {
  switch (error?.code) {
    case 'ROOM_FULL': return 'That table is full. Try another.';
    case 'ROOM_NOT_FOUND': return 'That table is no longer available.';
    case 'INSUFFICIENT_FUNDS': return 'Not enough Stacks for that table.';
    case 'ALREADY_IN_ROOM': return "You're already at that table.";
    case 'NO_SERVERS': return 'No tables open right now. Try again in a moment.';
    case 'ALLOCATION_MISSING': return 'That table is no longer reachable.';
    case 'CREATE_FAILED': return "Couldn't create the table. Try again.";
    case 'MODE_LOCKED': return 'That mode is not available yet.';
    case 'INVALID_BUY_IN': return 'That buy-in is not valid.';
    case 'BLOCKED': return "You can't join a table with someone you've blocked.";
    case 'NOT_AUTHENTICATED': return 'Please sign in again to play.';
    default: return error?.message || "Couldn't join the table. Try again.";
  }
}

/**
 * Start a game with the given mode
 */
async function startGame(
  mode: 'solo' | 'friends' | 'matchmaking',
  gameType: 'ceelo' | 'craps' | 'solo' = 'ceelo',
): Promise<void> {

  if (!gameStateManager || !uiManager) {
    console.error('[main.ts] Cannot start game - missing gameStateManager or uiManager');
    return;
  }

  // Track the live game type so the in-game manual opens to the right section.
  activeGameType = gameType;

  // Show loading screen (may already be visible from handleHomeAction)
  loadingScreen.show();

  // Hide main UI behind the loading screen
  uiManager.hide();

  // Show game container (make 3D scene visible behind loading screen)
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) {
    gameContainer.style.visibility = 'visible';
  }

  // Create a promise that resolves when scene setup completes
  const sceneReady = new Promise<void>(resolve => { sceneSetupResolve = resolve; });

  // Start the game — triggers 'game:started' which calls setupGameScene then resolves sceneReady
  gameStateManager.startGame({
    mode,
    gameType,
    // Solo starts in Hi-Lo (2 dice); the roll authorization drives the real
    // count per mode. Cee-lo is 3, craps 2.
    diceCount: gameType === 'ceelo' ? 3 : 2,
  });

  // Wait for both scene to fully load AND minimum display time
  await Promise.all([
    sceneReady,
    loadingScreen.waitForMinTime(),
  ]);

  // Scene is ready — hide loading screen
  await loadingScreen.hide();
}

// Start the game
main().catch(console.error);
